import React, { useState, useRef, useEffect, useCallback } from "react";
import { API, graphqlOperation } from "aws-amplify";
// Knowledge Graph imports - COMMENTED OUT
// import CandidateViewKnowledgeGraph from "../../components/KnowledgeGraph/CandidateViewKnowledgeGraph";
// import ErrorBoundary from "../../components/ErrorBoundary";
// Debug component for data inspection
// import DebugDataInspector from "../../components/DebugDataInspector";
// Import the Skills Radar Chart component
import SkillsRadarChart from "../../components/SkillsRadarChart";

// Import new evaluation components
import InterviewMetadataPanel from "../../components/InterviewMetadataPanel";
import RiskAssessmentAlert from "../../components/RiskAssessmentAlert";
import QuestionBreakdownCards from "../../components/QuestionBreakdownCards";
import DevelopmentRoadmap from "../../components/DevelopmentRoadmap";

import {
  listCandidateProfiles,
  listAdminAddCandidateContexts,
  listInterviewEvaluations,
} from "../../graphql/queries";
import Header from "../../components/header/Header";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Modal,
  Paper,
  Stack,
  Avatar,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  Chat as ChatIcon,
  FormatQuote as FormatQuoteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeDown as VolumeDownIcon,
  VolumeOff as VolumeOffIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Share as ShareIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp,
  DragIndicator as DragIndicatorIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import { Slider } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import ReactPlayer from "react-player";
// Import WebM seeking utilities
import {
  isWebMVideo,
  enhancedSeek,
  getWebMPlayerConfig,
  monitorSeeking,
  getVideoFormatInfo,
} from "../../utils/webmSeekingUtils";

// Import JWT auth service - STUB: Service file doesn't exist yet
// import BrowserJWTService from "../../services/browserJWTService";

// Stub for BrowserJWTService until actual service is created
class BrowserJWTService {
  constructor() {}
  async generateToken(payload, ttl) {
    console.warn('BrowserJWTService stub: generateToken called');
    return null;
  }
  async verifyToken(token) {
    console.warn('BrowserJWTService stub: verifyToken called');
    return null;
  }
}

// Create JWT service instance
const jwtAuthService = new BrowserJWTService();

// Enhanced utility functions for JWT-based public URL generation
const generatePublicProfileURL = async (email, slotId = null, baseURL = null, ttl = 600) => {
  try {
    // Use the integrated JWT auth service
    const result = jwtAuthService.generatePublicProfileURL(email, slotId, ttl, baseURL);
    
    if (!result.success) {
      console.error('❌ Failed to generate JWT public link:', result.error);
      // Fallback to legacy method if JWT fails
      return generateLegacyPublicProfileURL(email, slotId, baseURL);
    }

    console.log('✅ Generated JWT-based public profile URL:', result.url);
    return result.url;
    
  } catch (error) {
    console.error('❌ Error generating JWT public link:', error);
    // Fallback to legacy method if service fails
    return generateLegacyPublicProfileURL(email, slotId, baseURL);
  }
};

// Legacy fallback method (timestamp-based expiration)
const generateLegacyPublicProfileURL = (email, slotId = null, baseURL = null) => {
  console.warn('⚠️ Using legacy public profile URL generation');
  const base = baseURL || window.location.origin;
  const encodedEmail = btoa(email); // Base64 encode the email
  const timestamp = Date.now(); // Add current timestamp for expiration
  let url = `${base}/public-profile?e=${encodedEmail}&t=${timestamp}`;

  if (slotId) {
    url += `&slot=${encodeURIComponent(slotId)}`;
  }

  return url;
};

const generatePublicProfileURLForSharing = async (email, slotId = null, ttl = 600) => {
  return await generatePublicProfileURL(email, slotId, null, ttl);
};

const decodeEmailFromURL = (encodedEmail) => {
  try {
    if (/^[A-Za-z0-9+/]*={0,2}$/.test(encodedEmail)) {
      return atob(encodedEmail);
    } else {
      return encodedEmail; // Fallback for plain text
    }
  } catch (error) {
    console.error("Error decoding email:", error);
    return encodedEmail;
  }
};

// ChartJS imports removed - now using SkillsRadarChart component

// Create dark theme with enhanced z-index management and glassmorphism
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#e9c46a",
    },
    secondary: {
      main: "#2a9d8f",
    },
    background: {
      default: "#000000",
      paper: "rgba(0, 0, 0, 0.6)",
    },
  },
  typography: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  },
  components: {
    // Ensure all Material-UI popover components have high z-index
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: 100010, // Higher than our AI Score Panel
        },
        paper: {
          background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          zIndex: 100010,
        },
        paper: {
          background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          zIndex: 100010,
        },
        tooltip: {
          background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          border: "1px solid rgba(233, 196, 106, 0.25)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)",
          borderRadius: "12px",
          padding: "12px 16px",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          zIndex: 100010,
        },
        paper: {
          background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          zIndex: 100015, // Even higher for dialogs
        },
        paper: {
          background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 20px 50px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "20px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "14px",
          fontWeight: 500,
        },
      },
    },
  },
});

// Add this CustomHeader component after the theme definition
const CustomHeader = ({ fullName, email }) => {
  const navigate = useNavigate();

  return (
    <AppBar
      position="fixed"
      sx={{
        background: "rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(20px)",
        boxShadow: "none",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <Toolbar sx={{ padding: "8px 24px" }}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="back"
          onClick={() => navigate(-1)}
          sx={{
            mr: 2,
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.2)",
              transform: "scale(1.05)",
            },
            transition: "all 0.2s",
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          {fullName || "Candidate Profile"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mr: 2,
            opacity: 0.8,
            fontSize: "14px",
          }}
        >
          {email}
        </Typography>
      </Toolbar>
    </AppBar>
  );
};

// MODIFIED: Compact ProfileOverlay with all candidate details
const ProfileOverlay = ({
  fullName,
  photoURL,
  workStatus,
  keySkills,
  description,
  expanded,
  onMinimize,
  mobileNumber,
  email,
  linkedinURL,
  githubURL,
  currentCity,
  workPreference,
  preferredSalary,
  educationDetails,
  roleApplyFor,
}) => {
  const skillTokens = typeof keySkills === "string"
    ? keySkills
        .split(",")
        .map((skill) => skill.trim())
        .filter(Boolean)
    : [];
  const fallbackPills = [
    "Communication",
    "Problem Solving",
    "Leadership",
    "Culture Add",
  ];
  const displayPills = skillTokens.length ? skillTokens : fallbackPills;

  const cardPalette = [
    { accent: "#e9c46a", bg: "rgba(233,196,106,0.15)" },
    { accent: "#2a9d8f", bg: "rgba(42,157,143,0.15)" },
    { accent: "#f4a261", bg: "rgba(244,162,97,0.15)" },
    { accent: "#e76f51", bg: "rgba(231,111,81,0.15)" },
  ];

  const summaryText = description?.trim()
    ? description.trim()
    : "Interview notes will appear here once captured by the interviewer or AI assistant.";

  const initials = fullName
    ? fullName
        .split(" ")
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "CN";

  return (
    <Box
      sx={{
        position: "absolute",
        top: 64,
        left: 24,
        width: "340px",
        maxHeight: "60vh",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
        backdropFilter: "blur(32px)",
        WebkitBackdropFilter: "blur(32px)",
        borderRadius: "20px",
        border: "1px solid rgba(233, 196, 106, 0.22)",
        boxShadow:
          "0 25px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
        zIndex: 100030,
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: expanded ? "translateX(0)" : "translateX(-120%)",
        opacity: expanded ? 1 : 0,
        visibility: expanded ? "visible" : "hidden",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          position: "relative",
          padding: "12px 16px 10px",
          borderBottom: "1px solid rgba(233, 196, 106, 0.15)",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={onMinimize}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "rgba(255,255,255,0.7)",
            bgcolor: "rgba(255,255,255,0.08)",
            width: 28,
            height: 28,
            zIndex: 2,
            "&:hover": {
              bgcolor: "rgba(233,196,106,0.25)",
              color: "white",
            },
            transition: "all 0.2s",
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

  <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            src={photoURL || undefined}
            alt={fullName}
            variant="rounded"
            sx={{
              width: 54,
              height: 54,
              borderRadius: "14px",
              border: "2px solid rgba(233,196,106,0.4)",
              boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
              background:
                "linear-gradient(135deg, rgba(233,196,106,0.2) 0%, rgba(38,70,83,0.4) 100%)",
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "rgba(255,255,255,0.9)",
            }}
          >
            {photoURL ? null : initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                fontWeight: 600,
                fontSize: "13px",
                letterSpacing: "-0.01em",
                mb: 0.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fullName || "Candidate"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(233,196,106,0.85)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "8px",
                fontWeight: 600,
                display: "block",
              }}
            >
              {workStatus || "Status Pending"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "9px 14px 10px",
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255,255,255,0.03)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(233,196,106,0.3)",
            borderRadius: "10px",
            "&:hover": {
              background: "rgba(233,196,106,0.5)",
            },
          },
        }}
      >
        <Stack spacing={2}>
          {/* Role & Location */}
          {(roleApplyFor || currentCity) && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  letterSpacing: "0.15em",
                  fontSize: "9px",
                  fontWeight: 700,
                  mb: 1,
                  display: "block",
                }}
              >
                Position
              </Typography>
              <Stack spacing={0.8}>
                {roleApplyFor && (
                  <Box
                    sx={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(233,196,106,0.12)",
                      border: "1px solid rgba(233,196,106,0.25)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(233,196,106,0.9)",
                        fontSize: "8px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      Role
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontSize: "10px",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {roleApplyFor}
                    </Typography>
                  </Box>
                )}
                {currentCity && (
                  <Box
                    sx={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(231,111,81,0.12)",
                      border: "1px solid rgba(231,111,81,0.25)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(231,111,81,0.9)",
                        fontSize: "8px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      Location
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontSize: "10px",
                        fontWeight: 500,
                      }}
                    >
                      {currentCity}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Contact Information */}
          {(email || mobileNumber) && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  letterSpacing: "0.15em",
                  fontSize: "9px",
                  fontWeight: 700,
                  mb: 1,
                  display: "block",
                }}
              >
                Contact
              </Typography>
              <Stack spacing={0.8}>
                {email && (
                  <Box
                    sx={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(42,157,143,0.12)",
                      border: "1px solid rgba(42,157,143,0.25)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(42,157,143,0.9)",
                        fontSize: "8px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontSize: "10px",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {email}
                    </Typography>
                  </Box>
                )}
                {mobileNumber && (
                  <Box
                    sx={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(244,162,97,0.12)",
                      border: "1px solid rgba(244,162,97,0.25)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(244,162,97,0.9)",
                        fontSize: "8px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      Phone
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontSize: "10px",
                        fontWeight: 500,
                      }}
                    >
                      {mobileNumber}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Social Links */}
          {(linkedinURL || githubURL) && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  letterSpacing: "0.15em",
                  fontSize: "9px",
                  fontWeight: 700,
                  mb: 1,
                  display: "block",
                }}
              >
                Social Links
              </Typography>
              <Stack direction="row" spacing={0.8}>
                {linkedinURL && (
                  <Box
                    component="a"
                    href={linkedinURL.startsWith("http") ? linkedinURL : `https://${linkedinURL}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      flex: 1,
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(0,119,181,0.12)",
                      border: "1px solid rgba(0,119,181,0.25)",
                      textDecoration: "none",
                      textAlign: "center",
                      transition: "all 0.2s",
                      "&:hover": {
                        background: "rgba(0,119,181,0.25)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(0,119,181,1)",
                        fontSize: "10px",
                        fontWeight: 600,
                      }}
                    >
                      LinkedIn
                    </Typography>
                  </Box>
                )}
                {githubURL && (
                  <Box
                    component="a"
                    href={githubURL.startsWith("http") ? githubURL : `https://${githubURL}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      flex: 1,
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      textDecoration: "none",
                      textAlign: "center",
                      transition: "all 0.2s",
                      "&:hover": {
                        background: "rgba(255,255,255,0.25)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontSize: "10px",
                        fontWeight: 600,
                      }}
                    >
                      GitHub
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Work Preferences */}
          {(workPreference || preferredSalary) && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  letterSpacing: "0.15em",
                  fontSize: "9px",
                  fontWeight: 700,
                  mb: 1,
                  display: "block",
                }}
              >
                Preferences
              </Typography>
              <Stack spacing={0.8}>
                {workPreference && (
                  <Box
                    sx={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(42,157,143,0.12)",
                      border: "1px solid rgba(42,157,143,0.25)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(42,157,143,0.9)",
                        fontSize: "8px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      Work Mode
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontSize: "10px",
                        fontWeight: 500,
                      }}
                    >
                      {workPreference}
                    </Typography>
                  </Box>
                )}
                {preferredSalary && (
                  <Box
                    sx={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(244,162,97,0.12)",
                      border: "1px solid rgba(244,162,97,0.25)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(244,162,97,0.9)",
                        fontSize: "8px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      Expected Salary
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontSize: "10px",
                        fontWeight: 500,
                      }}
                    >
                      {preferredSalary}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Education */}
          {educationDetails && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  letterSpacing: "0.15em",
                  fontSize: "9px",
                  fontWeight: 700,
                  mb: 1,
                  display: "block",
                }}
              >
                Education
              </Typography>
              <Box
                sx={{
                  padding: "8px 10px",
                  borderRadius: "10px",
                  background: "rgba(233,196,106,0.12)",
                  border: "1px solid rgba(233,196,106,0.25)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.95)",
                    fontSize: "10px",
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  {educationDetails}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Skills Section */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "0.15em",
                fontSize: "9px",
                fontWeight: 700,
                mb: 1,
                display: "block",
              }}
            >
              Key Skills
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.7,
              }}
            >
              {displayPills.map((pill, index) => (
                <Chip
                  key={`${pill}-${index}`}
                  label={pill}
                  size="small"
                  sx={{
                    bgcolor: cardPalette[index % cardPalette.length].bg,
                    color: "rgba(255,255,255,0.92)",
                    borderRadius: "9px",
                    fontSize: "10px",
                    height: "24px",
                    fontWeight: 600,
                    border: `1px solid ${cardPalette[index % cardPalette.length].accent}44`,
                    letterSpacing: "0.02em",
                    "& .MuiChip-label": {
                      padding: "0 9px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "120px",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* AI Notes */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f4a261 0%, #f4d371 100%)",
              borderRadius: "14px",
              padding: "12px 14px",
              color: "#2d1f04",
              boxShadow: "0 10px 28px rgba(244,162,97,0.4)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.8} mb={0.8}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  fontSize: "9px",
                }}
              >
                AI Interview Notes
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                fontSize: "11px",
                lineHeight: 1.5,
                fontWeight: 500,
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              {summaryText}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

// Compact evaluation overlay mirrors the profile panel while surfacing key scoring insights.
const EvaluationOverlay = ({
  expanded,
  onMinimize,
  radarChartData,
  onViewFullEvaluation,
  evaluationData,
}) => {
  if (!evaluationData) {
    return null;
  }

  const overallScore = Number(evaluationData.overall_score) || null;
  const recommendation = evaluationData.recommendation || null;
  const confidence = evaluationData.confidence_level || null;
  const hireRationale = evaluationData.hire_rationale || evaluationData.detailed_feedback || null;
  const topStrengths = Array.isArray(evaluationData.key_strengths)
    ? evaluationData.key_strengths.filter(Boolean).slice(0, 3)
    : [];
  const developmentFocus = Array.isArray(evaluationData.areas_for_improvement)
    ? evaluationData.areas_for_improvement.filter(Boolean).slice(0, 3)
    : [];
  const hasChartData = radarChartData?.metrics?.length && radarChartData?.series?.[0]?.data?.length;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 64,
        right: 24,
        width: { xs: 320, sm: 340, md: 360 },
        maxHeight: "60vh",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(88,112,205,0.2) 42%, rgba(22,30,54,0.18) 100%)",
        backdropFilter: "blur(32px)",
        WebkitBackdropFilter: "blur(32px)",
        borderRadius: "20px",
        border: "1px solid rgba(102, 126, 234, 0.24)",
        boxShadow:
          "0 25px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
        zIndex: 100030,
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: expanded ? "translateX(0)" : "translateX(120%)",
        opacity: expanded ? 1 : 0,
        visibility: expanded ? "visible" : "hidden",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          padding: "12px 16px 10px",
          borderBottom: "1px solid rgba(102, 126, 234, 0.18)",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={onMinimize}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "rgba(255,255,255,0.7)",
            bgcolor: "rgba(255,255,255,0.08)",
            width: 28,
            height: 28,
            zIndex: 2,
            "&:hover": {
              bgcolor: "rgba(102,126,234,0.25)",
              color: "white",
            },
            transition: "all 0.2s",
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: "13px",
            letterSpacing: "0.02em",
          }}
        >
          Evaluation Snapshot
        </Typography>
        {recommendation && (
          <Typography
            variant="caption"
            sx={{
              color: "rgba(102,126,234,0.8)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontSize: "8px",
              fontWeight: 600,
              display: "block",
              mt: 0.5,
            }}
          >
            {recommendation}
            {confidence ? ` • ${confidence}` : ""}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "12px 16px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 1.8,
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255,255,255,0.03)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(102,126,234,0.3)",
            borderRadius: "10px",
            "&:hover": {
              background: "rgba(102,126,234,0.5)",
            },
          },
        }}
      >
        {(overallScore || recommendation) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "12px",
              border: "1px solid rgba(102,126,234,0.3)",
              background: "rgba(102,126,234,0.12)",
              px: 2,
              py: 1.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontWeight: 500,
                letterSpacing: "0.02em",
              }}
            >
              Overall Score
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(233,196,106,0.9)",
                fontWeight: 700,
              }}
            >
              {overallScore ? `${Math.round(overallScore)}%` : "—"}
            </Typography>
          </Box>
        )}

        {topStrengths.length > 0 && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "0.15em",
                fontSize: "9px",
                fontWeight: 700,
                mb: 1,
                display: "block",
              }}
            >
              Key Strengths
            </Typography>
            <Stack direction="row" spacing={0.8} flexWrap="wrap">
              {topStrengths.map((strength, index) => (
                <Chip
                  key={`${strength}-${index}`}
                  label={strength}
                  size="small"
                  sx={{
                    bgcolor: "rgba(102,126,234,0.18)",
                    color: "rgba(255,255,255,0.9)",
                    borderRadius: "9px",
                    fontSize: "10px",
                    height: "24px",
                    border: "1px solid rgba(102,126,234,0.35)",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {developmentFocus.length > 0 && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "0.15em",
                fontSize: "9px",
                fontWeight: 700,
                mb: 1,
                display: "block",
              }}
            >
              Focus Areas
            </Typography>
            <Stack spacing={0.7}>
              {developmentFocus.map((focus, index) => (
                <Box
                  key={`${focus}-${index}`}
                  sx={{
                    borderRadius: "10px",
                    border: "1px solid rgba(233,196,106,0.25)",
                    background: "rgba(233,196,106,0.12)",
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      lineHeight: 1.4,
                    }}
                  >
                    {focus}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {hireRationale && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "0.15em",
                fontSize: "9px",
                fontWeight: 700,
                mb: 1,
                display: "block",
              }}
            >
              Summary
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.5,
                background: "rgba(255,255,255,0.05)",
                borderRadius: "10px",
                px: 1.5,
                py: 1.2,
              }}
            >
              {hireRationale}
            </Typography>
          </Box>
        )}

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <Button
          variant="contained"
          onClick={onViewFullEvaluation}
          sx={{
            alignSelf: "stretch",
            background: "linear-gradient(108deg, #667EEA 0%, #8859B8 100%)",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            mt: 0.5,
            "&:hover": {
              background: "linear-gradient(108deg, #7689ec 0%, #9666c4 100%)",
              boxShadow: "0 6px 20px rgba(102, 126, 234, 0.35)",
            },
          }}
        >
          View Full Evaluation
        </Button>
      </Box>
    </Box>
  );
};

// Charts View Overlay - Side-by-side panel for detailed chart visualization
const ChartsOverlay = ({
  expanded,
  onMinimize,
  radarChartData,
  technicalRadarChartData,
  evaluationData,
}) => {
  if (!evaluationData) {
    return null;
  }

  const hasSoftSkillsData = radarChartData?.metrics?.length && radarChartData?.series?.[0]?.data?.length;
  const hasTechnicalSkillsData = technicalRadarChartData?.metrics?.length && technicalRadarChartData?.series?.[0]?.data?.length;
  const hasAnyChartData = hasSoftSkillsData || hasTechnicalSkillsData;

  return (
    <Box
      sx={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: expanded 
          ? "translate(-50%, -50%) scale(1)" 
          : "translate(-50%, -50%) scale(0.95)",
        width: { xs: "90vw", sm: "700px", md: "800px" },
        height: "85vh",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(62,124,132,0.22) 40%, rgba(18,36,44,0.18) 100%)",
        backdropFilter: "blur(32px)",
        WebkitBackdropFilter: "blur(32px)",
        borderRadius: "24px",
        border: "1px solid rgba(42, 157, 143, 0.28)",
        boxShadow:
          "0 26px 70px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.14)",
        zIndex: 100035,
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        opacity: expanded ? 1 : 0,
        visibility: expanded ? "visible" : "hidden",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "relative",
          padding: "16px 20px",
          borderBottom: "1px solid rgba(42, 157, 143, 0.25)",
          flexShrink: 0,
          background: "rgba(42, 157, 143, 0.08)",
        }}
      >
        <IconButton
          onClick={onMinimize}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            bgcolor: "rgba(255,255,255,0.08)",
            width: 32,
            height: 32,
            zIndex: 2,
            "&:hover": {
              bgcolor: "rgba(42,157,143,0.3)",
              color: "white",
            },
            transition: "all 0.2s",
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #2a9d8f 0%, #264653 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(42, 157, 143, 0.3)",
            }}
          >
            <BarChartIcon sx={{ fontSize: 22, color: "white" }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                fontWeight: 700,
                fontSize: "16px",
                letterSpacing: "0.02em",
              }}
            >
              Performance Analytics
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(42,157,143,0.9)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "10px",
                fontWeight: 600,
              }}
            >
              Visual Evaluation Breakdown
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255,255,255,0.03)",
            borderRadius: "10px",
            margin: "10px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(42,157,143,0.4)",
            borderRadius: "10px",
            "&:hover": {
              background: "rgba(42,157,143,0.6)",
            },
          },
        }}
      >
        {hasAnyChartData ? (
          <>
            {/* Soft Skills Radar Chart - Takes 50% of viewport */}
            {hasSoftSkillsData && (
              <Box
                sx={{
                  minHeight: "calc(85vh - 100px)",
                  height: "calc(85vh - 100px)",
                  display: "flex",
                  flexDirection: "column",
                  borderBottom: hasTechnicalSkillsData ? "1px solid rgba(42, 157, 143, 0.2)" : "none",
                  p: 3,
                  pb: hasTechnicalSkillsData ? 4 : 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.95)",
                    fontWeight: 700,
                    mb: 2,
                    letterSpacing: "0.04em",
                    fontSize: "18px",
                  }}
                >
                  Soft Skills Analysis
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 0,
                    borderRadius: "20px",
                    border: "1px solid rgba(42,157,143,0.28)",
                    background: "linear-gradient(135deg, rgba(255,255,255,0.09) 0%, rgba(46,108,116,0.2) 60%, rgba(20,40,52,0.18) 100%)",
                    p: 2,
                  }}
                >
                  <SkillsRadarChart
                    series={radarChartData.series}
                    radar={{
                      metrics: radarChartData.metrics,
                      max: radarChartData.max,
                      label: "Soft Skills",
                    }}
                    height="100%"
                  />
                </Box>
              </Box>
            )}

            {/* Technical Skills Radar Chart - Takes 50% of viewport */}
            {hasTechnicalSkillsData && (
              <Box
                sx={{
                  minHeight: "calc(85vh - 100px)",
                  height: "calc(85vh - 100px)",
                  display: "flex",
                  flexDirection: "column",
                  p: 3,
                  pt: hasSoftSkillsData ? 4 : 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.95)",
                    fontWeight: 700,
                    mb: 2,
                    letterSpacing: "0.04em",
                    fontSize: "18px",
                  }}
                >
                  Technical Skills Analysis
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 0,
                    borderRadius: "20px",
                    border: "1px solid rgba(42,157,143,0.28)",
                    background: "linear-gradient(135deg, rgba(255,255,255,0.09) 0%, rgba(46,108,116,0.2) 60%, rgba(20,40,52,0.18) 100%)",
                    p: 2,
                  }}
                >
                  <SkillsRadarChart
                    series={technicalRadarChartData.series}
                    radar={{
                      metrics: technicalRadarChartData.metrics,
                      max: technicalRadarChartData.max,
                      label: "Technical Skills",
                    }}
                    height="100%"
                  />
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              borderRadius: "20px",
              border: "1px dashed rgba(42,157,143,0.3)",
              background: "rgba(42,157,143,0.08)",
              p: 6,
              textAlign: "center",
            }}
          >
            <BarChartIcon
              sx={{
                fontSize: 64,
                color: "rgba(42,157,143,0.4)",
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.7)",
                fontWeight: 600,
                mb: 1,
              }}
            >
              No Chart Data Available
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.5)",
                maxWidth: "400px",
                margin: "0 auto",
              }}
            >
              Evaluation metrics will appear here once the interview has been analyzed.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Update the VideoPlayer component with more advanced controls and slot-aware video extraction
const VideoPlayer = ({
  profiles,
  interviewEvaluations,
  selectedSlotId = null,
}) => {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [played, setPlayed] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const controlsTimeout = useRef(null);
  const [shareOpen, setShareOpen] = useState(false); // State for share panel
  const shareButtonRef = useRef(null); // ADDED: Ref for anchoring share panel
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false); // State for playback speed menu
  const speedButtonRef = useRef(null); // Ref for anchoring speed menu
  const [isWebMFormat, setIsWebMFormat] = useState(false);
  const [seekingStatus, setSeekingStatus] = useState(null);
  const pendingSeekSecondsRef = useRef(null);
  const pendingSeekFractionRef = useRef(null);
  const html5VideoRef = useRef(null);
  const { email } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const slotId = searchParams.get("slotId") || searchParams.get("slot-id");

  // ... (All the existing useEffects and handler functions like getVideoUrl, handleMouseMove, handleSeekChange etc. remain the same) ...
  // ... (No changes needed for the logic inside the component, only for the JSX render output)
  // ... (Keeping all existing functions for brevity)

  const handleShare = async (platform) => {
    try {
      const shareUrl = await generatePublicProfileURLForSharing(email, slotId);
      switch (platform) {
        case "linkedin":
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
              shareUrl
            )}`
          );
          break;
        case "facebook":
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              shareUrl
            )}`
          );
          break;
        case "instagram":
          break;
        case "whatsapp":
          window.open(
            `https://api.whatsapp.com/send?text=${encodeURIComponent(shareUrl)}`
          );
          break;
        case "copy":
          navigator.clipboard.writeText(shareUrl);
          break;
        default:
          break;
      }
      setShareOpen(false);
    } catch (error) {
      console.error('❌ Error sharing profile:', error);
      // Fallback to legacy sharing if JWT fails
      const legacyUrl = generateLegacyPublicProfileURL(email, slotId);
      navigator.clipboard.writeText(legacyUrl);
      setShareOpen(false);
    }
  };
  const getVideoUrl = () => {
    if (selectedSlotId && interviewEvaluations?.length > 0) {
      const slotEvaluation = interviewEvaluations.find(
        (evaluation) =>
          evaluation.interviewSlotID === selectedSlotId && evaluation.videoURL
      );
      if (slotEvaluation?.videoURL) {
        return slotEvaluation.videoURL;
      }
    }
    if (selectedSlotId && interviewEvaluations?.length > 0) {
      const slotEvaluation = interviewEvaluations.find(
        (evaluation) => evaluation.interviewSlotID === selectedSlotId
      );
      if (slotEvaluation?.evaluation) {
        try {
          const parsedEval = parseEvaluationData(slotEvaluation.evaluation);
          if (parsedEval && typeof parsedEval === "object") {
            const videoFields = [
              "videoURL",
              "video_url",
              "videoUrl",
              "recordingUrl",
              "recording_url",
              "interviewVideo",
              "interview_video",
            ];
            for (const field of videoFields) {
              if (parsedEval[field] && typeof parsedEval[field] === "string") {
                return parsedEval[field];
              }
            }
          }
        } catch (e) {}
      }
    }
    if (interviewEvaluations?.length > 0) {
      const evaluationWithVideo = interviewEvaluations.find(
        (evaluation) => evaluation.videoURL
      );
      if (evaluationWithVideo?.videoURL) {
        return evaluationWithVideo.videoURL;
      }
    }
    if (interviewEvaluations?.length > 0) {
      for (const evaluation of interviewEvaluations) {
        if (evaluation.evaluation) {
          try {
            const parsedEval = parseEvaluationData(evaluation.evaluation);
            if (parsedEval && typeof parsedEval === "object") {
              const videoFields = [
                "videoURL",
                "video_url",
                "videoUrl",
                "recordingUrl",
                "recording_url",
                "interviewVideo",
                "interview_video",
              ];
              for (const field of videoFields) {
                if (
                  parsedEval[field] &&
                  typeof parsedEval[field] === "string"
                ) {
                  return parsedEval[field];
                }
              }
            }
          } catch (e) {}
        }
      }
    }
    if (profiles?.length > 0 && profiles[0]?.candidateVideo) {
      return profiles[0].candidateVideo;
    }
    return null;
  };
  const videoUrl = getVideoUrl();
  const parseEvaluationData = (evaluationData) => {
    if (!evaluationData) return null;
    try {
      if (typeof evaluationData === "string") {
        return JSON.parse(evaluationData);
      } else if (typeof evaluationData === "object") {
        return evaluationData;
      }
    } catch (error) {
      console.error("Error parsing evaluation data:", error);
    }
    return null;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
    }
    controlsTimeout.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };
  const handlePlayPause = () => {
    setPlaying(!playing);
  };
  const handleSeekMouseDown = () => {
    setSeeking(true);
  };
  const handleSeekChange = (e, newValue) => {
    // Only update visual state during seeking, don't actually seek until mouse up
    if (seeking) {
      const v = Array.isArray(newValue) ? newValue[0] : newValue;
      const parsedValue = Number(v);
      if (isFinite(parsedValue) && parsedValue >= 0 && parsedValue <= 100) {
        setPlayed(parsedValue / 100);
      }
    }
  };
  const getBestDuration = () => {
    try {
      const rpDuration =
        typeof playerRef.current?.getDuration === "function"
          ? playerRef.current.getDuration()
          : 0;
      if (isFinite(rpDuration) && rpDuration > 0) return rpDuration;
      const vid = getHtml5Video();
      const vDur = vid?.duration;
      if (isFinite(vDur) && vDur > 0) return vDur;
    } catch (e) {}
    return 0;
  };
  const handleSeekMouseUp = (e, newValue) => {
    setSeeking(false);
    const v = Array.isArray(newValue) ? newValue[0] : newValue;
    const durNow = getBestDuration();
    if (playerRef.current && isFinite(durNow) && durNow > 0) {
      const parsedValue = Number(v);
      if (isFinite(parsedValue) && parsedValue >= 0 && parsedValue <= 100) {
        const seekPosition = parsedValue / 100;
        if (
          isFinite(seekPosition) &&
          seekPosition >= 0 &&
          seekPosition <= 1
        ) {
          if (isWebMFormat) {
            setSeekingStatus("seeking");
            const dur = durNow;
            const safeSeek = Math.max(0, Math.min(seekPosition, 0.999));
            enhancedSeek(playerRef, safeSeek, dur, true)
              .then((success) => {
                setSeekingStatus(success ? "success" : "failed");
                if (success) {
                  setPlayed(safeSeek);
                }
                monitorSeeking(
                  playerRef,
                  safeSeek,
                  (seekSuccess) => {
                    setTimeout(() => setSeekingStatus(null), 1500);
                    if (!seekSuccess) {
                      try {
                        const internalPlayer =
                          playerRef.current.getInternalPlayer();
                        if (
                          internalPlayer &&
                          internalPlayer.currentTime !== undefined
                        ) {
                          const targetTime =
                            safeSeek * (getBestDuration() || duration);
                          internalPlayer.currentTime = targetTime;
                          setPlayed(safeSeek);
                        }
                      } catch (backupError) {
                        console.error(
                          "🎬 Backup seek strategy also failed:",
                          backupError
                        );
                      }
                    }
                  },
                  (currentPos, targetPos, diff) => {
                    if (Math.abs(diff) < 0.05) {
                      setPlayed(currentPos);
                    }
                  }
                );
              })
              .catch((error) => {
                console.error("🎬 Enhanced seeking failed:", error);
                setSeekingStatus("failed");
                try {
                  playerRef.current.seekTo(safeSeek, "fraction");
                  setPlayed(safeSeek);
                } catch (fallbackError) {
                  console.error(
                    "🎬 Fallback seeking also failed:",
                    fallbackError
                  );
                }
              });
          } else {
            try {
              const safeSeek = Math.max(0, Math.min(seekPosition, 0.999));
              playerRef.current.seekTo(safeSeek, "fraction");
              setPlayed(safeSeek);
            } catch (error) {
              console.error("🎬 Standard seeking failed:", error);
            }
          }
        }
      }
    } else {
      if (playerRef.current) {
        try {
          const internalPlayer = playerRef.current.getInternalPlayer();
          if (internalPlayer) {
            let finiteDur = Number(internalPlayer.duration);
            if (!isFinite(finiteDur) || finiteDur <= 0) {
              try {
                const seekable = internalPlayer.seekable;
                if (seekable && seekable.length > 0) {
                  finiteDur = Number(seekable.end(seekable.length - 1));
                }
              } catch (_) {}
            }
            if (isFinite(finiteDur) && finiteDur > 0) {
              setDuration(finiteDur);
              const vNum = Array.isArray(newValue) ? newValue[0] : newValue;
              const parsed = Number(vNum);
              if (isFinite(parsed)) {
                pendingSeekSecondsRef.current =
                  Math.max(0, Math.min(0.999, parsed / 100)) * finiteDur;
              }
            } else {
              const vNum = Array.isArray(newValue) ? newValue[0] : newValue;
              const parsed = Number(vNum);
              if (isFinite(parsed)) {
                pendingSeekFractionRef.current = Math.max(
                  0,
                  Math.min(0.999, parsed / 100)
                );
              }
            }
          }
        } catch (error) {
          console.error(
            "🎬 Error getting duration from internal player:",
            error
          );
        }
      }
    }
  };
  const handleVolumeChange = (e, newValue) => {
    // Handle both single values and arrays (for different slider components)
    const v = Array.isArray(newValue) ? newValue[0] : newValue;
    const parsedValue = parseFloat(v);
    
    console.log('🔊 Volume change:', parsedValue);
    
    if (isFinite(parsedValue) && parsedValue >= 0 && parsedValue <= 100) {
      const volumeLevel = parsedValue / 100;
      setVolume(volumeLevel);
      
      // Auto-unmute when volume is increased from 0
      if (parsedValue > 0 && muted) {
        setMuted(false);
      }
      // Auto-mute when volume is set to 0
      else if (parsedValue === 0 && !muted) {
        setMuted(true);
      }
    }
  };
  const handleMuteToggle = () => {
    console.log('🔇 Mute toggle:', !muted);
    setMuted(!muted);
    
    // If unmuting and volume is 0, set it to a reasonable level
    if (muted && volume === 0) {
      setVolume(0.5); // Set to 50% when unmuting from 0 volume
    }
  };
  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);
  };
  const toggleFullScreen = () => {
    const element = playerContainerRef.current;
    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setFullscreen(false);
    }
  };
  const formatTime = (seconds) => {
    if (!isFinite(seconds) || seconds < 0) return "00:00";
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, "0");
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, "0")}:${ss}`;
    }
    return `${mm}:${ss}`;
  };
  const handleDuration = (duration) => {
    if (isFinite(duration) && duration > 0) {
      setDuration(duration);
      setLoading(false); // Video metadata loaded
    } else {
      if (playerRef.current) {
        try {
          const internalPlayer = playerRef.current.getInternalPlayer();
          if (
            internalPlayer &&
            internalPlayer.duration &&
            isFinite(internalPlayer.duration)
          ) {
            const videoDuration = internalPlayer.duration;
            setDuration(videoDuration);
            setLoading(false);
          } else {
            setDuration(0);
          }
        } catch (error) {
          setDuration(0);
        }
      } else {
        setDuration(0);
      }
    }
  };

  const handleReady = () => {
    console.log('🎬 Video ready to play');
    setLoading(false);
    setBuffering(false);
  };

  const handleBuffer = () => {
    console.log('🎬 Video buffering...');
    setBuffering(true);
  };

  const handleBufferEnd = () => {
    console.log('🎬 Video buffer end');
    setBuffering(false);
  };
  const handleProgress = (state) => {
    // Only update progress if not seeking to avoid interference with user input
    if (
      !seeking &&
      state &&
      isFinite(state.played) &&
      state.played >= 0 &&
      state.played <= 1
    ) {
      setPlayed(state.played);
    }
    
    // Debug logging (remove in production)
    if (state && state.playedSeconds) {
      console.log('🎬 Video progress:', {
        played: state.played,
        playedSeconds: state.playedSeconds,
        loadedSeconds: state.loadedSeconds,
        seeking: seeking
      });
    }
  };
  const handleEnded = () => {
    setPlaying(false);
    setPlayed(0);
  };
  const getHtml5Video = () => {
    try {
      return playerRef.current?.getInternalPlayer() || null;
    } catch (e) {
      return null;
    }
  };

  // Add keyboard controls and enhanced error handling
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle keys when video player is active and not typing in inputs
      if (e.target.tagName.toLowerCase() === 'input' || e.target.tagName.toLowerCase() === 'textarea') {
        return;
      }

      switch (e.key.toLowerCase()) {
        case ' ':
        case 'k':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'arrowleft':
          e.preventDefault();
          // Skip back 10 seconds
          if (playerRef.current) {
            const currentTime = playerRef.current.getCurrentTime() || 0;
            const newTime = Math.max(0, currentTime - 10);
            const duration = getBestDuration() || 0;
            if (duration > 0) {
              const seekPosition = newTime / duration;
              handleSeekMouseUp(null, seekPosition * 100);
            }
          }
          break;
        case 'arrowright':
          e.preventDefault();
          // Skip forward 10 seconds
          if (playerRef.current) {
            const currentTime = playerRef.current.getCurrentTime() || 0;
            const duration = getBestDuration() || 0;
            const newTime = Math.min(duration, currentTime + 10);
            if (duration > 0) {
              const seekPosition = newTime / duration;
              handleSeekMouseUp(null, seekPosition * 100);
            }
          }
          break;
        case 'm':
          e.preventDefault();
          handleMuteToggle();
          break;
        case 'f':
          e.preventDefault();
          toggleFullScreen();
          break;
        case 'arrowup':
          e.preventDefault();
          // Volume up by 10%
          const newVolumeUp = Math.min(100, (volume * 100) + 10);
          handleVolumeChange(null, newVolumeUp);
          break;
        case 'arrowdown':
          e.preventDefault();
          // Volume down by 10%
          const newVolumeDown = Math.max(0, (volume * 100) - 10);
          handleVolumeChange(null, newVolumeDown);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [playing, volume, muted, playerRef.current]);

  // Enhanced error handling and retry mechanism
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;

    const handlePlayerError = (error) => {
      console.error('🎬 Video Player Error:', error);
      
      if (retryCount < maxRetries) {
        retryCount++;
        console.log(`🎬 Retrying video load (${retryCount}/${maxRetries})`);
        
        // Retry after a short delay
        setTimeout(() => {
          if (playerRef.current) {
            // Force a reload by setting played to 0 and playing to false, then true
            setPlaying(false);
            setPlayed(0);
            setTimeout(() => setPlaying(true), 100);
          }
        }, 1000 * retryCount); // Exponential backoff
      }
    };

    // Listen for errors from the HTML5 video element
    const videoElement = getHtml5Video();
    if (videoElement) {
      videoElement.addEventListener('error', handlePlayerError);
      return () => videoElement.removeEventListener('error', handlePlayerError);
    }
  }, [videoUrl, playerRef.current]);

  // WebM format detection
  useEffect(() => {
    if (videoUrl) {
      const isWebM = isWebMVideo(videoUrl);
      setIsWebMFormat(isWebM);
      
      console.log('🎬 Video format detected:', {
        url: videoUrl,
        isWebM: isWebM,
        format: getVideoFormatInfo(videoUrl)
      });
    }
  }, [videoUrl]);

  return (
    <>
      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <Box
        ref={playerContainerRef}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
        height: "100vh",
        zIndex: 0,
        backgroundColor: "black",
        cursor: showControls ? "default" : "none",
      }}
      onMouseMove={handleMouseMove}
      onClick={() => (showControls ? setShowControls(true) : handlePlayPause())}
    >
      {videoUrl ? (
        <VideoErrorBoundary>
          <ReactPlayer
            ref={playerRef}
            url={videoUrl}
            width="100%"
            height="100%"
            className="react-player"
            style={{ objectFit: "cover" }}
            playing={playing}
            volume={volume}
            muted={muted}
            playbackRate={playbackRate}
            onDuration={handleDuration}
            onProgress={handleProgress}
            onEnded={handleEnded}
            onReady={handleReady}
            onBuffer={handleBuffer}
            onBufferEnd={handleBufferEnd}
            config={getWebMPlayerConfig(videoUrl)}
          />
          
          {/* Loading/Buffering overlay */}
          {(loading || buffering) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'white',
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: '4px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '4px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  mb: 2,
                }}
              />
              <Box sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                {loading ? 'Loading video...' : 'Buffering...'}
              </Box>
            </Box>
          )}
          
          {/* Big Play Button (when paused) - CENTERED AND SIMPLIFIED */}
          {!playing && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1,
                transition: "opacity 0.3s ease",
              }}
            >
              <Box
                sx={{
                  borderRadius: "50%",
                  background: "rgba(0, 0, 0, 0.75)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: 80,
                  height: 80,
                  cursor: "pointer",
                  border: "2px solid rgba(255,255,255,0.3)",
                  "&:hover": {
                    background: "rgba(233, 196, 106, 0.9)",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 60, color: "white" }} />
              </Box>
            </Box>
          )}

          {/* Custom Video Controls - REBUILT FOR CLEANER UI */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 100%)",
              padding: "40px 20px 10px",
              transition: "opacity 0.3s ease",
              opacity: showControls || !playing ? 1 : 0, // Keep controls visible when paused
              pointerEvents: showControls || !playing ? "auto" : "none",
              zIndex: 2,
            }}
            onClick={(e) => e.stopPropagation()} // Prevent player click when interacting with controls
          >
            {/* Timeline Analysis Integration */}
            {(() => {
              // Get the current evaluation's timestamp_analysis
              const currentEvaluation = interviewEvaluations?.find(
                (evaluation) => evaluation.interviewSlotID === selectedSlotId
              );
              
              let timestampAnalysis = null;
              
              if (currentEvaluation?.evaluation) {
                try {
                  const parsedEval = typeof currentEvaluation.evaluation === 'string' 
                    ? JSON.parse(currentEvaluation.evaluation) 
                    : currentEvaluation.evaluation;
                  
                  timestampAnalysis = parsedEval?.timestamp_analysis;
                } catch (error) {
                  console.error('❌ Error parsing evaluation:', error);
                }
              }

              // Helper function to convert timestamp string to seconds
              const timeToSeconds = (timeStr) => {
                if (!timeStr || timeStr === "00:00:000" || timeStr === "00:00:00") return 0;

                const parts = timeStr.split(':');

                if (parts.length === 3) {
                  const [first, second, third] = parts;

                  if (third.length === 3 && parseInt(first, 10) < 60) {
                    const minutes = parseInt(first, 10) || 0;
                    const seconds = parseInt(second, 10) || 0;
                    const milliseconds = parseInt(third, 10) || 0;
                    return minutes * 60 + seconds + milliseconds / 1000;
                  }

                  const hours = parseInt(first, 10) || 0;
                  const minutes = parseInt(second, 10) || 0;
                  const seconds = parseFloat(third) || 0;
                  return hours * 3600 + minutes * 60 + seconds;
                }

                return 0;
              };

              const formatTime = (seconds) => {
                const mins = Math.floor(seconds / 60);
                const secs = Math.floor(seconds % 60);
                return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
              };

              const getPerformanceColor = (level) => {
                switch (level?.toLowerCase()) {
                  case 'strong':
                  case 'excellent':
                    return '#2a9d8f';
                  case 'adequate':
                  case 'good':
                    return '#e9c46a';
                  case 'weak':
                  case 'poor':
                    return '#e76f51';
                  default:
                    return '#f4a261';
                }
              };

              const hasValidTimestamps = timestampAnalysis?.some(q => {
                const isValid = q.start_time && 
                               q.start_time !== "00:00:000" && 
                               q.start_time !== "00:00:00" &&
                               q.start_time !== "0:00:00";
                return isValid;
              });

              // Build timeline entries if we have valid timestamps
              const rawTimelineEntries = hasValidTimestamps ? timestampAnalysis.map((question) => {
                const startSeconds = timeToSeconds(question.start_time);
                const endSeconds = timeToSeconds(question.end_time);
                const performanceColor = getPerformanceColor(question.performance_level);
                const durationSeconds = Math.max(
                  endSeconds - startSeconds,
                  question.duration_seconds || 0
                );

                return {
                  ...question,
                  startSeconds,
                  endSeconds,
                  performanceColor,
                  durationSeconds,
                };
              }) : [];

              const totalSeconds = duration || 0;
              const sortedTimelineEntries = rawTimelineEntries
                .slice()
                .sort((a, b) => a.startSeconds - b.startSeconds);

              const entriesWithPercents = sortedTimelineEntries.map((entry) => {
                const rawStartPercent = totalSeconds ? (entry.startSeconds / totalSeconds) * 100 : 0;
                const rawEndPercent = totalSeconds ? (entry.endSeconds / totalSeconds) * 100 : rawStartPercent + 5;
                const startPercent = Math.max(0, Math.min(100, rawStartPercent));
                const endPercent = Math.max(startPercent, Math.min(100, rawEndPercent));
                const centerPercent = Math.min(98, Math.max(2, (startPercent + endPercent) / 2));

                return {
                  ...entry,
                  startPercent,
                  endPercent,
                  centerPercent,
                };
              });

              const MIN_MARKER_GAP = 8;
              const MIN_BOUND = 2;
              const MAX_BOUND = 98;

              const markerPositions = entriesWithPercents.map((entry) =>
                Math.min(MAX_BOUND, Math.max(MIN_BOUND, entry.centerPercent))
              );

              for (let i = 1; i < markerPositions.length; i += 1) {
                if (markerPositions[i] - markerPositions[i - 1] < MIN_MARKER_GAP) {
                  markerPositions[i] = markerPositions[i - 1] + MIN_MARKER_GAP;
                }
              }

              for (let i = markerPositions.length - 2; i >= 0; i -= 1) {
                if (markerPositions[i + 1] - markerPositions[i] < MIN_MARKER_GAP) {
                  markerPositions[i] = markerPositions[i + 1] - MIN_MARKER_GAP;
                }
              }

              if (markerPositions.length > 0) {
                const overflowRight = markerPositions[markerPositions.length - 1] - MAX_BOUND;
                if (overflowRight > 0) {
                  for (let i = 0; i < markerPositions.length; i += 1) {
                    markerPositions[i] -= overflowRight;
                  }
                }

                const overflowLeft = MIN_BOUND - markerPositions[0];
                if (overflowLeft > 0) {
                  for (let i = 0; i < markerPositions.length; i += 1) {
                    markerPositions[i] += overflowLeft;
                  }
                }
              }

              const timelineEntries = entriesWithPercents.map((entry, idx) => ({
                ...entry,
                displayPercent: Math.min(MAX_BOUND, Math.max(MIN_BOUND, markerPositions[idx] ?? entry.centerPercent)),
              }));

              return (
                <Box sx={{ mb: 1, position: 'relative' }}>
                  {/* Timeline boxes above the progress bar */}
                  {timelineEntries.length > 0 && (
                    <Box 
                      sx={{ 
                        position: 'relative', 
                        height: 72, 
                        mb: 1,
                        pointerEvents: showControls ? 'auto' : 'none',
                      }}
                    >
                      {timelineEntries.map((entry, index) => {
                        const summaryText = entry.question_summary || entry.answer_summary;
                        const tooltipContent = (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 260 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: '#e9c46a', lineHeight: 1.2 }}
                            >
                              {`Q${entry.question_number} • ${entry.skill_tested || entry.topic || 'Question'}`}
                            </Typography>
                            {summaryText && (
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}
                              >
                                {summaryText.slice(0, 160)}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255,255,255,0.65)', letterSpacing: '0.02em' }}
                            >
                              {`${formatTime(entry.startSeconds)} – ${formatTime(entry.endSeconds)} • Score ${entry.performance_score || 0}%`}
                            </Typography>
                            {entry.red_flags?.length ? (
                              <Typography variant="caption" sx={{ color: '#f4a261' }}>
                                ⚠️ {entry.red_flags.length} red flag{entry.red_flags.length > 1 ? 's' : ''}
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: '#2a9d8f' }}>
                                ✅ No red flags detected
                              </Typography>
                            )}
                          </Box>
                        );

                        // Calculate staggered delay: each marker appears/disappears sequentially
                        const animationDelay = showControls ? `${index * 0.08}s` : `${index * 0.08}s`;

                        return (
                          <Tooltip key={`${entry.question_number}-${index}`} title={tooltipContent} placement="top" arrow>
                            <Box
                              onClick={() => {
                                if (playerRef.current && entry.startSeconds >= 0) {
                                  playerRef.current.seekTo(entry.startSeconds, 'seconds');
                                  setPlaying(true);
                                }
                              }}
                              sx={{
                                position: 'absolute',
                                left: `${entry.displayPercent}%`,
                                bottom: 20,
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                opacity: showControls ? 1 : 0,
                                transition: `opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay}, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), filter 0.35s cubic-bezier(0.4, 0, 0.2, 1)`,
                                '&:hover': {
                                  transform: 'translateX(-50%) translateY(-8px) scale(1.15)',
                                  filter: 'brightness(1.2)',
                                },
                                '&:hover .marker-circle': {
                                  boxShadow: `0 12px 32px ${entry.performanceColor}60, 0 0 0 4px ${entry.performanceColor}20`,
                                  transform: 'scale(1.1)',
                                },
                                '&:hover .marker-line': {
                                  height: 32,
                                  boxShadow: `0 0 12px ${entry.performanceColor}50`,
                                },
                              }}
                            >
                              <Box
                                className="marker-circle"
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  background: `linear-gradient(135deg, ${entry.performanceColor} 0%, ${entry.performanceColor}cc 100%)`,
                                  border: `2px solid ${entry.performanceColor}aa`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#0b0d0f',
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  boxShadow: `0 6px 18px ${entry.performanceColor}35`,
                                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                  backdropFilter: 'blur(4px)',
                                }}
                              >
                                Q{entry.question_number}
                              </Box>
                              <Box
                                className="marker-line"
                                sx={{
                                  width: 2,
                                  height: 24,
                                  background: `linear-gradient(180deg, ${entry.performanceColor}90, transparent)`,
                                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                  borderRadius: '2px',
                                }}
                              />
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  )}

                  {/* Progress Bar with segment overlays */}
                  <Box sx={{ position: 'relative' }}>
                    <Slider
                      min={0}
                      max={100}
                      step={0.1}
                      value={
                        isFinite(played)
                          ? Math.max(0, Math.min(100, played * 100))
                          : 0
                      }
                      onChange={handleSeekChange}
                      onMouseDown={handleSeekMouseDown}
                      onMouseUp={handleSeekMouseUp}
                      onChangeCommitted={handleSeekMouseUp}
                      sx={{
                        height: 4,
                        color: "#e9c46a",
                        "& .MuiSlider-thumb": {
                          width: 14,
                          height: 14,
                          "&:hover, &.Mui-focusVisible": {
                            boxShadow: "0px 0px 0px 8px rgba(233, 196, 106, 0.16)",
                          },
                        },
                        "& .MuiSlider-rail": {
                          opacity: 0.28,
                        },
                      }}
                    />
                    
                    {/* Segment indicators on the progress bar */}
                    {timelineEntries.map((entry, index) => {
                      const startPercent = entry.startPercent;
                      const endPercent = Math.max(entry.startPercent, entry.endPercent || entry.startPercent + 2);
                      const widthPercent = Math.max(endPercent - startPercent, 1);

                      return (
                        <Box
                          key={`segment-${entry.question_number}-${index}`}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: `${startPercent}%`,
                            width: `${widthPercent}%`,
                            height: 4,
                            background: `linear-gradient(90deg, ${entry.performanceColor}90, ${entry.performanceColor}40)`,
                            borderRadius: 2,
                            pointerEvents: 'none',
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}

            {/* Progress Bar (moved inside the timeline analysis block above) */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Left Controls */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  onClick={handlePlayPause}
                  size="small"
                  sx={{
                    color: "white",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "rgba(0, 0, 0, 0.7)",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.85)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {playing ? (
                    <PauseIcon fontSize="medium" />
                  ) : (
                    <PlayArrowIcon fontSize="medium" />
                  )}
                </IconButton>

                <IconButton
                  onClick={handleMuteToggle}
                  size="small"
                  sx={{
                    color: "white",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "rgba(0, 0, 0, 0.7)",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.85)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {muted ? (
                    <VolumeOffIcon />
                  ) : volume > 0.5 ? (
                    <VolumeUpIcon />
                  ) : (
                    <VolumeDownIcon />
                  )}
                </IconButton>

                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={
                    muted
                      ? 0
                      : isFinite(volume)
                      ? Math.max(0, Math.min(100, volume * 100))
                      : 0
                  }
                  onChange={handleVolumeChange}
                  onMouseDown={() => console.log('🔊 Volume slider mouse down')}
                  onMouseUp={() => console.log('🔊 Volume slider mouse up')}
                  sx={{
                    width: 100,
                    ml: 1,
                    color: "white",
                    "& .MuiSlider-rail": { opacity: 0.28 },
                    height: 4,
                    "& .MuiSlider-thumb": {
                      width: 10,
                      height: 10,
                    },
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{ ml: 2, color: "white", minWidth: "90px" }}
                >
                  {formatTime(
                    isFinite(played) && isFinite(duration)
                      ? played * duration
                      : 0
                  )}{" "}
                  / {formatTime(isFinite(duration) ? duration : 0)}
                </Typography>
              </Box>

              {/* Right Controls */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  ref={speedButtonRef}
                  variant="text"
                  size="small"
                  onClick={() => setSpeedMenuOpen(!speedMenuOpen)}
                  sx={{
                    color: "white",
                    minWidth: "auto",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  {playbackRate}x
                </Button>

                {/* Playback Speed Menu */}
                {speedMenuOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "100%",
                      right: 120,
                      mb: 2,
                      background: "rgba(0, 0, 0, 0.95)",
                      borderRadius: 2,
                      p: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      zIndex: 100020,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.9)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      minWidth: 80,
                    }}
                  >
                    {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                      <Button
                        key={speed}
                        size="small"
                        onClick={() => {
                          handlePlaybackRateChange(speed);
                          setSpeedMenuOpen(false);
                        }}
                        sx={{
                          color: playbackRate === speed ? "#e9c46a" : "white",
                          fontSize: "0.8rem",
                          fontWeight: playbackRate === speed ? 600 : 400,
                          textTransform: "none",
                          justifyContent: "flex-start",
                          "&:hover": {
                            background: "rgba(233, 196, 106, 0.2)",
                          },
                        }}
                      >
                        {speed}x
                      </Button>
                    ))}
                  </Box>
                )}

                <IconButton
                  ref={shareButtonRef}
                  onClick={() => setShareOpen(!shareOpen)}
                  size="small"
                  sx={{
                    color: "white",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "rgba(0, 0, 0, 0.7)",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.85)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <ShareIcon />
                </IconButton>

                {shareOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "100%", // Position above the bar
                      right: 20,
                      mb: 2,
                      background: "rgba(0, 0, 0, 0.95)",
                      borderRadius: 2,
                      p: 1,
                      display: "flex",
                      gap: 1,
                      zIndex: 100020, // High z-index to ensure it's above everything
                      boxShadow: "0 8px 32px rgba(0,0,0,0.9)",
                      border: "1px solid rgba(255,255,255,0.15)",
                    }}
                  >
                    <IconButton
                      onClick={() => handleShare("linkedin")}
                      sx={{ color: "white" }}
                    >
                      <LinkedInIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleShare("facebook")}
                      sx={{ color: "white" }}
                    >
                      <FacebookIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleShare("whatsapp")}
                      sx={{ color: "white" }}
                    >
                      <WhatsApp />
                    </IconButton>
                    <IconButton
                      onClick={() => handleShare("copy")}
                      sx={{ color: "white" }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Box>
                )}

                <IconButton
                  onClick={toggleFullScreen}
                  size="small"
                  sx={{
                    color: "white",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "rgba(0, 0, 0, 0.7)",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.85)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </VideoErrorBoundary>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "white",
          }}
        >
          <Typography variant="h6">No video available</Typography>
        </Box>
      )}
    </Box>
    </>
  );
};

class VideoErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Video Player Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            color: "white",
          }}
        >
          <Typography variant="h6" color="error">
            Error loading video
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => this.setState({ hasError: false })}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ height: "100%", overflow: "auto" }}
    >
      {value === index && <>{children}</>}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Custom query that doesn't request the undefined fields
const fixedListAdminAddCandidateContexts = /* GraphQL */ `
  query FixedListAdminAddCandidateContexts(
    $filter: ModelAdminAddCandidateContextFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listAdminAddCandidateContexts(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        candidateEmail
        candidateName
        candidateVideo
        candidateContextTextFile
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const CompanyCandidateViewProfilev1Wrapper = ({
  emailProp,
  slotIdProp = null,
  skipFetch = false,
  isPublicAccess = false,
  isAdminAccess = false,
  ...otherProps
}) => {
  const params = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Extract email and slot ID from various sources
  const email = emailProp || params.email;
  const slotId = slotIdProp || searchParams.get("slotId") || searchParams.get("slot-id");

  const [candidateData, setCandidateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Extract email from various sources
    const finalEmail = emailProp || params.email;

    // Skip data fetching if skipFetch is true (for test mode)
    if (skipFetch) {
      setError("No candidate data available in test mode.");
      setLoading(false);
      return;
    }

    // For production mode, require email parameter
    if (!finalEmail) {
      setError("No email provided. Please provide a valid candidate email.");
      setLoading(false);
      return;
    }

    const fetchCandidateData = async () => {
      try {
        console.log("🔍 ===== FETCH CANDIDATE DATA DEBUG =====");
        console.log("📧 Email:", finalEmail);
        console.log("🎯 SlotId:", slotId || "none");
        
        // Fetch candidate profile
        let candidateProfile = null;
        let nextToken = null;

        console.log("📋 Step 1: Fetching candidate profile...");
        do {
          const candidateProfileResponse = await API.graphql(
            graphqlOperation(listCandidateProfiles, {
              filter: { email: { eq: finalEmail } },
              limit: 100,
              nextToken: nextToken,
            })
          );

          const items =
            candidateProfileResponse.data.listCandidateProfiles.items;
          console.log("✅ Candidate profiles found:", items.length);
          
          if (items.length > 0) {
            candidateProfile = items[0];
            console.log("✅ Using profile:", candidateProfile.email);
            break;
          } else {
            nextToken =
              candidateProfileResponse.data.listCandidateProfiles.nextToken;
          }
        } while (nextToken); // If no candidate profile found, show error
        if (!candidateProfile) {
          throw new Error(`No candidate profile found for the provided email address.`);
        }

        // Fetch profiles using fixed custom query that doesn't request undefined fields
        nextToken = null;
        const adminContextItems = [];

        console.log("📋 Step 2: Fetching admin contexts...");
        do {
          const adminContextResponse = await API.graphql(
            graphqlOperation(fixedListAdminAddCandidateContexts, {
              // Use fixed query here
              filter: { candidateEmail: { eq: finalEmail } },
              limit: 100,
              nextToken: nextToken,
            })
          );

          adminContextItems.push(
            ...adminContextResponse.data.listAdminAddCandidateContexts.items
          );
          nextToken =
            adminContextResponse.data.listAdminAddCandidateContexts.nextToken;
        } while (nextToken);
        console.log("✅ Admin contexts found:", adminContextItems.length);

        // Fetch interview evaluations with slot-based filtering
        nextToken = null;
        const interviewEvaluationsItems = [];

        console.log("📋 Step 3: Fetching interview evaluations...");
        do {
          // Build the filter based on whether slotId is provided
          const evaluationFilter = { candidateEmail: { eq: finalEmail } };
          if (slotId) {
            evaluationFilter.interviewSlotID = { eq: slotId };
          }
          
          console.log("🔍 Evaluation filter:", JSON.stringify(evaluationFilter, null, 2));

          const interviewEvaluationsResponse = await API.graphql(
            graphqlOperation(listInterviewEvaluations, {
              filter: evaluationFilter,
              limit: 100,
              nextToken: nextToken,
            })
          );

          const fetchedItems = interviewEvaluationsResponse.data.listInterviewEvaluations.items;
          console.log("✅ Evaluations fetched in this batch:", fetchedItems.length);
          
          if (fetchedItems.length > 0) {
            console.log("📊 Sample evaluation data:");
            fetchedItems.forEach((item, index) => {
              console.log(`   [${index}] ID: ${item.id}`);
              console.log(`   [${index}] SlotID: ${item.interviewSlotID}`);
              console.log(`   [${index}] Email: ${item.candidateEmail}`);
              console.log(`   [${index}] Has evaluation: ${item.evaluation ? 'YES' : 'NO'}`);
              console.log(`   [${index}] Evaluation type: ${typeof item.evaluation}`);
              if (item.evaluation) {
                console.log(`   [${index}] Evaluation length: ${item.evaluation.length} chars`);
                console.log(`   [${index}] Evaluation preview: ${item.evaluation.substring(0, 100)}...`);
              }
            });
          }

          interviewEvaluationsItems.push(...fetchedItems);
          nextToken =
            interviewEvaluationsResponse.data.listInterviewEvaluations
              .nextToken;
        } while (nextToken);

        console.log("✅ Total evaluations found:", interviewEvaluationsItems.length);

        // Compile all data into one object
        const compiledData = {
          ...candidateProfile,
          profiles: adminContextItems,
          interviewEvaluations: interviewEvaluationsItems,
          selectedSlotId: slotId, // Include slot information in compiled data
        };
        
        console.log("✅ Compiled data structure:");
        console.log("   - Profile fields:", Object.keys(candidateProfile).join(", "));
        console.log("   - Profiles count:", adminContextItems.length);
        console.log("   - Evaluations count:", interviewEvaluationsItems.length);
        console.log("   - Selected slot ID:", slotId || "none");
        console.log("🔍 ===== FETCH COMPLETED =====");
        
        setCandidateData(compiledData);
      } catch (error) {
        console.error("❌ Error in fetchCandidateData:", error);
        console.error("❌ Error details:", error.message);
        console.error("❌ Error stack:", error.stack);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateData();
  }, [email, emailProp, params.email, slotId, skipFetch]); // Updated dependencies to include slotId

  const VideoDebugInfo = () => {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          bgcolor: "rgba(0,0,0,0.8)",
          color: "white",
          p: 2,
          maxWidth: "500px",
          zIndex: 9999,
        }}
      >
        <Typography variant="h6">Video URLs</Typography>
        <pre
          style={{ fontSize: "12px", overflow: "auto", maxHeight: "200px" }}
        >
          {JSON.stringify(
            {
              profiles: candidateData.profiles?.map((p) => ({
                id: p.id,
                video: p.candidateVideo,
              })),
              evaluations: candidateData.interviewEvaluations?.map((e) => ({
                id: e.id,
                video: e.videoURL,
              })),
            },
            null,
            2
          )}
        </pre>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "black",
        }}
      >
        <CircularProgress sx={{ color: "#9900FF" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "black",
          color: "white",
          p: 3,
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ color: "#9900FF", mb: 2 }}>
          Profile Not Available
        </Typography>
        <Typography variant="body1" color="rgba(255,255,255,0.8)" align="center" sx={{ mb: 3, maxWidth: 400 }}>
          {error.includes("No candidate profile found") 
            ? "We couldn't find any profile data for this candidate. The profile may not exist or may have been removed."
            : error.includes("No email provided")
            ? "A valid candidate email is required to view this profile."
            : error.includes("No candidate data available in test mode")
            ? "Test mode is enabled but no candidate data is available for display."
            : "There was an issue loading the candidate profile. Please try again later."
          }
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ 
            mt: 2,
            bgcolor: "#9900FF",
            "&:hover": {
              bgcolor: "#7700DD"
            }
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (!candidateData) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "black",
          color: "white",
          p: 3,
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ color: "#9900FF", mb: 2 }}>
          No Profile Data
        </Typography>
        <Typography variant="body1" color="rgba(255,255,255,0.8)" align="center">
          Unable to load candidate profile information at this time.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Admin Access Indicator */}
      {isAdminAccess && (
        <Box
          sx={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
            color: "white",
            padding: "12px 20px",
            borderRadius: "25px",
            fontWeight: "bold",
            fontSize: "14px",
            boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
            border: "2px solid rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            gap: 1,
            "&::before": {
              content: '"👑"',
              fontSize: "16px",
            },
          }}
        >
          ADMIN ACCESS - No Expiration
        </Box>
      )}
      <CompanyCandidateViewProfilev1
        {...candidateData}
        isPublicAccess={isPublicAccess}
        isAdminAccess={isAdminAccess}
      />
      {/* {process.env.NODE_ENV === 'development' && <VideoDebugInfo />} */}
    </>
  );
};

// Add this function to calculate the AI score from evaluations
const calculateAIScore = (evaluations) => {
  if (
    !evaluations ||
    evaluations.length === 0 ||
    !evaluations[0]?.evaluation
  ) {
    return 0;
  }

  try {
    const evalData =
      typeof evaluations[0].evaluation === "string"
        ? JSON.parse(evaluations[0].evaluation)
        : evaluations[0].evaluation;

    // Check if overall_score exists and use it directly
    if (evalData.overall_score && typeof evalData.overall_score === "number") {
      return Math.min(100, Math.max(0, evalData.overall_score));
    }

    // Filter out non-scorable fields for calculation
    const excludedFields = [
      "question_performance",
      "interview_metadata",
      "detailed_feedback",
      "areas_for_improvement",
      "candidate_strengths",
      "recommendation",
      "confidence_level",
      "overall_score",
    ];

    // Calculate score from scorable criteria
    let totalScore = 0;
    let scorableCount = 0;

    Object.entries(evalData).forEach(([key, value]) => {
      if (!excludedFields.includes(key)) {
        let score = 0;

        // Handle different data structures
        if (typeof value === "object" && value !== null) {
          // If it's an object with score property
          if (value.hasOwnProperty("score")) {
            score = parseFloat(value.score || 0);
          }
        } else if (typeof value === "number") {
          // If it's a direct numeric value
          score = value;
        }

        if (score > 0) {
          totalScore += score;
          scorableCount++;
        }
      }
    });

    if (scorableCount === 0) {
      return 0;
    }

    // Calculate average and convert to percentage if needed
    let averageScore = totalScore / scorableCount;

    // If scores seem to be out of 100, use as is
    // If scores seem to be out of 10 or less, scale to 100
    if (averageScore <= 10) {
      averageScore = (averageScore / 10) * 100;
    }

    return Math.min(100, Math.max(0, Math.round(averageScore)));
  } catch (error) {
    console.error("Error calculating AI score:", error);
    return 0;
  }
};

// Add this new component
const RelatedCandidates = ({ candidates, onCandidateClick }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 16, // Keep original top position
        left: "50%", // Center horizontally
        transform: "translateX(-50%)", // Center horizontally
        display: "flex",
        flexDirection: "row", // Change to row layout
        // gap: 3,
        zIndex: 2,
      }}
    >
      {candidates.slice(0, 4).map((candidate, index) => (
        <Box
          key={candidate.id}
          sx={{
            position: "relative",
            cursor: "pointer",
            transition: "all 0.3s ease",
            marginLeft: "-8px",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => onCandidateClick(candidate)}
        >
          <Box
            component="img"
            src={candidate.photoURL}
            alt={candidate.fullName}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: "2px solid",
              borderColor:
                hoveredIndex === index
                  ? "#667EEA"
                  : "rgba(255, 255, 255, 0.3)",
              transition: "border-color 0.3s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
              },
            }}
          />

          {/* Hover Info Card - Changed position to bottom */}
          {hoveredIndex === index && (
            <Box
              sx={{
                position: "absolute",
                top: "calc(100% + 8px)",
                left: "50%",
                transform: "translateX(-50%)",
                width: 160,
                height: 160,
                background: "rgba(0,0,0,0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 1.5,
                border: "1px solid rgba(102, 126, 234, 0.12)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                zIndex: 100020, // High z-index to ensure it's above everything including AI Score Panel
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ color: "white", fontWeight: 700, mb: 0.5 }}
              >
                {candidate.fullName}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#667EEA", fontSize: "0.75rem", mb: 1 }}
              >
                {candidate.role}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Exp
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white", fontWeight: 700 }}>
                    {candidate.experience}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Fit
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white", fontWeight: 700 }}>
                    {candidate.fitScore}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

// Draggable Toggle Buttons Component
const DraggableToggleButtons = ({
  profileExpanded,
  chartExpanded,
  chartsViewExpanded,
  onProfileToggle,
  onChartToggle,
  onChartsViewToggle,
  hasEvaluations,
}) => {
  const [position, setPosition] = useState({ x: window.innerWidth - 180, y: window.innerHeight / 2 - 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - 160, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 160, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  return (
    <Box
      sx={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 100015,
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
    >
      <Stack
        spacing={1.5}
        sx={{
          background: "linear-gradient(135deg, rgba(14,16,32,0.95) 0%, rgba(10,10,24,0.92) 100%)",
          backdropFilter: "blur(20px)",
          borderRadius: "16px",
          padding: "12px",
          border: "1px solid rgba(102, 126, 234, 0.3)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        }}
      >
        {/* Profile Button */}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onProfileToggle();
          }}
          sx={{
            width: 48,
            height: 48,
            background: profileExpanded
              ? "linear-gradient(135deg, #e9c46a 0%, #f4a261 100%)"
              : "rgba(233, 196, 106, 0.15)",
            color: profileExpanded ? "#000" : "#e9c46a",
            border: profileExpanded
              ? "2px solid #e9c46a"
              : "2px solid rgba(233, 196, 106, 0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              background: profileExpanded
                ? "linear-gradient(135deg, #f4d371 0%, #f4a261 100%)"
                : "rgba(233, 196, 106, 0.25)",
              transform: "scale(1.1)",
              boxShadow: "0 4px 16px rgba(233, 196, 106, 0.4)",
            },
          }}
        >
          <PersonIcon sx={{ fontSize: 24 }} />
        </IconButton>

        {/* Evaluation Button */}
        {hasEvaluations && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onChartToggle();
            }}
            sx={{
              width: 48,
              height: 48,
              background: chartExpanded
                ? "linear-gradient(135deg, #667EEA 0%, #8859B8 100%)"
                : "rgba(102, 126, 234, 0.15)",
              color: chartExpanded ? "#fff" : "#667EEA",
              border: chartExpanded
                ? "2px solid #667EEA"
                : "2px solid rgba(102, 126, 234, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: chartExpanded
                  ? "linear-gradient(135deg, #7689ec 0%, #9666c4 100%)"
                  : "rgba(102, 126, 234, 0.25)",
                transform: "scale(1.1)",
                boxShadow: "0 4px 16px rgba(102, 126, 234, 0.4)",
              },
            }}
          >
            <AssessmentIcon sx={{ fontSize: 24 }} />
          </IconButton>
        )}

        {/* Charts View Button */}
        {hasEvaluations && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onChartsViewToggle();
            }}
            sx={{
              width: 48,
              height: 48,
              background: chartsViewExpanded
                ? "linear-gradient(135deg, #2a9d8f 0%, #264653 100%)"
                : "rgba(42, 157, 143, 0.15)",
              color: chartsViewExpanded ? "#fff" : "#2a9d8f",
              border: chartsViewExpanded
                ? "2px solid #2a9d8f"
                : "2px solid rgba(42, 157, 143, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: chartsViewExpanded
                  ? "linear-gradient(135deg, #35b3a3 0%, #2f5461 100%)"
                  : "rgba(42, 157, 143, 0.25)",
                transform: "scale(1.1)",
                boxShadow: "0 4px 16px rgba(42, 157, 143, 0.4)",
              },
            }}
          >
            <BarChartIcon sx={{ fontSize: 24 }} />
          </IconButton>
        )}

        {/* Drag Handle Indicator */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 20,
            opacity: 0.5,
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.5)" }} />
        </Box>
      </Stack>
    </Box>
  );
};

// Update the existing evaluation tab to show a more detailed radar chart from evaluation data
const CompanyCandidateViewProfilev1 = ({
  email,
  fullName,
  photoURL,
  workStatus,
  description,
  keySkills,
  mobileNumber,
  profiles,
  interviewEvaluations,
  githubURL,
  linkedinURL,
  selectedSlotId, // Add selectedSlotId propF
  // Additional fields from GraphQL response
  id,
  userID,
  gender,
  matchedPercentage,
  currentCity,
  educationDetails,
  workPreference,
  preferredSalary,
  resumeURL,
  isPublic,
  t_c,
  roleApplyFor,
  address,
  interviewScheduleCount,
  private_Candidate,
  private_Candidate_Release,
  workMode,
  workCategory,
  coverPhoto,
  candidateData,
  createdAt,
  updatedAt,
  isPublicAccess = false, // New prop for public access mode
  isAdminAccess = false, // New prop for admin access mode
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("evaluation");
  const [chartExpanded, setChartExpanded] = useState(false); // Evaluation text overlay
  const [profileExpanded, setProfileExpanded] = useState(false); // Profile overlay
  const [chartsViewExpanded, setChartsViewExpanded] = useState(false); // Charts overlay
  // const [knowledgeGraphExpanded, setKnowledgeGraphExpanded] = useState(true); // COMMENTED OUT
  const videoRef = useRef(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playerRef = useRef(null);
  const navigate = useNavigate();
  // Add these new states at the top:
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  // const [showKnowledgeGraph, setShowKnowledgeGraph] = useState(false); // COMMENTED OUT

  // Iframe detection and public access management
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    // Detect if component is running inside an iframe
    const detectIframe = () => {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true; // If we can't access parent, we're likely in an iframe
      }
    };

    setIsInIframe(detectIframe());

    // Add iframe-specific styling if needed
    if (detectIframe() || isPublicAccess) {
      document.body.style.margin = "0";
      document.body.style.padding = "0";
      // Prevent scroll issues in iframe
      document.documentElement.style.overflow = "auto";
    }
  }, [isPublicAccess]);

  // Helper function to safely parse evaluation data
  const parseEvaluationData = (evaluationData) => {
    if (!evaluationData) return null;

    try {
      if (typeof evaluationData === "string") {
        return JSON.parse(evaluationData);
      } else if (typeof evaluationData === "object") {
        return evaluationData;
      }
    } catch (error) {
      console.error("Error parsing evaluation data:", error);
    }

    return null;
  };

  // Utility function to get current evaluation data (slot-specific or first available)
  const getCurrentEvaluationData = () => {
    if (!interviewEvaluations || interviewEvaluations.length === 0) {
      return null;
    }

    // If slot ID is specified, try to find evaluation for that slot
    if (selectedSlotId) {
      const slotEvaluation = interviewEvaluations.find(
        (evaluation) => evaluation.interviewSlotID === selectedSlotId
      );
      if (slotEvaluation) {
        return slotEvaluation;
      }
    }

    // Fallback to first evaluation
    return interviewEvaluations[0];
  };

  const currentEvaluation = getCurrentEvaluationData();
  const evaluationAvailable = Boolean(currentEvaluation?.evaluation);

  // Helper function to prepare candidate data for knowledge graph - COMMENTED OUT
  /*
  const prepareCandidateDataForKG = () => {
    return {
      email: email,
      fullName: fullName,
      photoURL: photoURL,
      workStatus: workStatus,
      description: description,
      keySkills: keySkills,
      mobileNumber: mobileNumber,
      githubURL: githubURL,
      linkedinURL: linkedinURL,
      profiles: profiles,
      interviewEvaluations: interviewEvaluations
    };
  };
  */
  // Helper function to open knowledge graph page (for production mode) - COMMENTED OUT
  /*
  const openKnowledgeGraphPage = () => {
    const candidateData = prepareCandidateDataForKG();
    
    // Store candidate data in localStorage for the knowledge graph page
    localStorage.setItem('kg_candidate_data', JSON.stringify(candidateData));
    
    // Navigate to the production knowledge graph route
    navigate(`/candidate-knowledge-graph/${encodeURIComponent(email)}`);
  };
  */

// Enhanced Radar chart data generation for MUI X Charts
  // MODIFIED: This function now prioritizes soft skills for the new evaluation format.
  const getRadarChartData = (skillType = 'soft') => {
    // Default empty data
    const defaultData = {
      series: [{ label: "Performance Score", data: [] }],
      metrics: [],
      max: 10,
    };

    const evaluation = getCurrentEvaluationData();
    
    if (!evaluation?.evaluation) {
      return defaultData;
    }

    try {
      const evalData = parseEvaluationData(evaluation.evaluation);

      if (!evalData) {
        return defaultData;
      }

      // NEW FORMAT CHECK: Handle technical_skills and soft_skills based on skillType
      if (skillType === 'technical' && evalData.technical_skills && typeof evalData.technical_skills === 'object' && Object.keys(evalData.technical_skills).length > 0) {
        const metrics = Object.keys(evalData.technical_skills).map(key => 
          key.split(/[_\s]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
        );
        const scores = Object.values(evalData.technical_skills).map(score =>
          Math.min(10, Math.max(0, parseFloat(score || 0) / 10)) // Scale from 0-100 to 0-10
        );
        
        return {
          series: [{ label: "Technical Skills", data: scores }],
          metrics: metrics,
          max: 10,
        };
      }

      if (skillType === 'soft' && evalData.soft_skills && typeof evalData.soft_skills === 'object' && Object.keys(evalData.soft_skills).length > 0) {
        const metrics = Object.keys(evalData.soft_skills).map(key => 
          key.split(/[_\s]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
        );
        const scores = Object.values(evalData.soft_skills).map(score =>
          Math.min(10, Math.max(0, parseFloat(score || 0) / 10)) // Scale from 0-100 to 0-10
        );
        
        return {
          series: [{ label: "Soft Skills", data: scores }],
          metrics: metrics,
          max: 10,
        };
      }

      // Fallback: if specific skill type not found, try the other type
      if (skillType === 'technical' && evalData.soft_skills && typeof evalData.soft_skills === 'object' && Object.keys(evalData.soft_skills).length > 0) {
        const metrics = Object.keys(evalData.soft_skills).map(key => 
          key.split(/[_\s]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
        );
        const scores = Object.values(evalData.soft_skills).map(score =>
          Math.min(10, Math.max(0, parseFloat(score || 0) / 10))
        );
        
        return {
          series: [{ label: "Soft Skills", data: scores }],
          metrics: metrics,
          max: 10,
        };
      }

      if (skillType === 'soft' && evalData.technical_skills && typeof evalData.technical_skills === 'object' && Object.keys(evalData.technical_skills).length > 0) {
        const metrics = Object.keys(evalData.technical_skills).map(key => 
          key.split(/[_\s]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
        );
        const scores = Object.values(evalData.technical_skills).map(score =>
          Math.min(10, Math.max(0, parseFloat(score || 0) / 10))
        );
        
        return {
          series: [{ label: "Technical Skills", data: scores }],
          metrics: metrics,
          max: 10,
        };
      }


      // --- OLD FORMAT FALLBACK ---
      // Filter criteria to only include scorable ones
      const excludedFields = [
        "question_performance",
        "interview_metadata",
        "detailed_feedback",
        "areas_for_improvement",
        "candidate_strengths",
        "recommendation",
        "confidence_level",
      ];

      const criteriaKeys = Object.keys(evalData).filter((key) => {
        if (excludedFields.includes(key)) return false;
        const data = evalData[key];
        return (
          typeof data === "number" ||
          (typeof data === "object" &&
            data !== null &&
            (data.hasOwnProperty("score") || data.hasOwnProperty("comments")))
        );
      });

      if (criteriaKeys.length === 0) {
        return defaultData;
      }

      // Map the actual criteria from the evaluation data with better formatting
      const metrics = criteriaKeys.map((key) => {
        // Format the key for better display (e.g., "technical_skills" -> "Technical Skills")
        let formattedLabel = key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        // Handle common abbreviations and improve readability
        const replacements = {
          Ai: "AI",
          Api: "API",
          Ui: "UI",
          Ux: "UX",
          Sql: "SQL",
          Html: "HTML",
          Css: "CSS",
          Javascript: "JavaScript",
          Typescript: "TypeScript",
        };

        Object.entries(replacements).forEach(([old, new_]) => {
          formattedLabel = formattedLabel.replace(new RegExp(old, "g"), new_);
        });

        return formattedLabel;
      });

      // Extract scores from each criteria and scale to 0-10
      const scores = criteriaKeys.map((key) => {
        const criteriaData = evalData[key];

        // Handle both object format (with score/max_score) and direct numeric values
        if (typeof criteriaData === "object" && criteriaData !== null) {
          const score = parseFloat(criteriaData.score || 0);
          const maxScore = parseFloat(criteriaData.max_score || 10);
          // Scale score to 0-10 range
          const scaledScore = (score / maxScore) * 10;
          return Math.min(10, scaledScore); // Cap at 10
        } else {
          // Direct numeric value - assume it's on a 0-100 scale and scale to 0-10
          const directScore = parseFloat(criteriaData || 0);
          // Scale from 0-100 to 0-10
          const scaledScore = (directScore / 100) * 10;
          return Math.min(10, Math.max(0, scaledScore)); // Cap at 10 and floor at 0
        }
      });

      const finalData = {
        series: [{ label: "Performance Score", data: scores }],
        metrics: metrics,
        max: 10,
      };
      
      return finalData;
    } catch (error) {
      console.error("Error parsing evaluation data for chart:", error);
      return defaultData;
    }
  };

  // Get radar chart data
  const radarChartData = getRadarChartData('soft');

  // Helper function to get category description
  const getCategoryDescription = (category, evaluationData) => {
    try {
      const evalData =
        typeof evaluationData === "string"
          ? JSON.parse(evaluationData)
          : evaluationData;
      const matchingKey = Object.keys(evalData).find((key) => key === category);

      if (!matchingKey) return "No details available";

      return evalData[matchingKey].comments || "No comments provided";
    } catch (error) {
      console.error("Error getting category description:", error);
      return "Error retrieving details";
    }
  };

  // Now update the evaluation tab content in renderTabContent
  const renderTabContent = () => {
    // Compact card styles matching overlay design
    const evaluationCardStyles = {
      p: "12px 16px",
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      borderRadius: "12px",
      border: "1px solid rgba(102, 126, 234, 0.2)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      display: "flex",
      flexDirection: "column",
      gap: 1.2,
      transition: "all 0.2s ease",
      '&:hover': {
        borderColor: "rgba(102, 126, 234, 0.35)",
        backgroundColor: "rgba(255, 255, 255, 0.12)",
      },
    };

    const headingStyles = {
      color: "rgba(102,126,234,0.95)",
      fontWeight: 600,
      fontSize: "12px",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    };

    switch (activeTab) {
      case "evaluation":
        return (
          <Box
            sx={{
              p: "16px",
              height: "100%",
              overflow: "auto",
            }}
          >
            {currentEvaluation?.evaluation ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {/* NEW: Performance Assessment (includes recommendation, confidence, and risk) */}
                {(() => {
                  const parsedEvaluation = parseEvaluationData(currentEvaluation.evaluation);
                  if (!parsedEvaluation?.interview_metadata?.risk_assessment) return null;
                  
                  return (
                    <RiskAssessmentAlert 
                      recommendation={parsedEvaluation.recommendation || 'PENDING'}
                      confidenceLevel={parsedEvaluation.confidence_level || 'MEDIUM'}
                      overallScore={parsedEvaluation.overall_score || 0}
                      riskAssessment={parsedEvaluation.interview_metadata.risk_assessment}
                    />
                  );
                })()}

                {/* NEW: Interview Metadata Panel */}
                {(() => {
                  const parsedEvaluation = parseEvaluationData(currentEvaluation.evaluation);
                  return parsedEvaluation?.interview_metadata ? (
                    <InterviewMetadataPanel metadata={parsedEvaluation.interview_metadata} />
                  ) : null;
                })()}

                {/* Question Analysis Section */}
                {(() => {
                  const parsedEvaluation = parseEvaluationData(
                    currentEvaluation.evaluation
                  );
                  
                  // NEW FORMAT: Check for question_analysis
                  const questionAnalysis = parsedEvaluation?.question_analysis;
                  
                  if (questionAnalysis && typeof questionAnalysis === 'object') {
                    return (
                      <Box sx={evaluationCardStyles}>
                        <Typography variant="overline" sx={headingStyles}>
                          Response Quality
                        </Typography>

                        {/* Compact Performance Metrics */}
                        <Box 
                          sx={{ 
                            display: "grid", 
                            gridTemplateColumns: "repeat(2, 1fr)", 
                            gap: 1,
                            mt: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              p: "10px 12px",
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              borderRadius: "8px",
                              textAlign: "center",
                              border: "1px solid rgba(102, 126, 234, 0.15)",
                            }}
                          >
                            <Typography
                              sx={{ 
                                color: "white", 
                                fontWeight: 700,
                                fontSize: "18px",
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              {questionAnalysis.total_questions || 0}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ 
                                color: "rgba(255,255,255,0.6)",
                                fontSize: "10px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              Total
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              p: "10px 12px",
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              borderRadius: "8px",
                              textAlign: "center",
                              border: "1px solid rgba(102, 126, 234, 0.15)",
                            }}
                          >
                            <Typography
                              sx={{ 
                                color: "white", 
                                fontWeight: 700,
                                fontSize: "18px",
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              {questionAnalysis.strong_responses || 0}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ 
                                color: "rgba(255,255,255,0.6)",
                                fontSize: "10px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              Strong
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              p: "10px 12px",
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              borderRadius: "8px",
                              textAlign: "center",
                              border: "1px solid rgba(102, 126, 234, 0.15)",
                            }}
                          >
                            <Typography
                              sx={{ 
                                color: "white", 
                                fontWeight: 700,
                                fontSize: "18px",
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              {questionAnalysis.adequate_responses || 0}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ 
                                color: "rgba(255,255,255,0.6)",
                                fontSize: "10px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              Adequate
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              p: "10px 12px",
                              backgroundColor: "rgba(255, 255, 255, 0.04)",
                              borderRadius: "8px",
                              textAlign: "center",
                              border: "1px solid rgba(102, 126, 234, 0.15)",
                            }}
                          >
                            <Typography
                              sx={{ 
                                color: "white", 
                                fontWeight: 700,
                                fontSize: "18px",
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              {questionAnalysis.weak_responses || 0}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ 
                                color: "rgba(255,255,255,0.6)",
                                fontSize: "10px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              Weak
                            </Typography>
                          </Box>
                        </Box>

                        {/* Compact Topics */}
                        {(questionAnalysis.excelled_topics?.length > 0 || questionAnalysis.challenging_topics?.length > 0) && (
                          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {/* Excelled Topics */}
                            {questionAnalysis.excelled_topics && questionAnalysis.excelled_topics.length > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "rgba(102,126,234,0.8)",
                                    fontSize: "10px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    mb: 0.8,
                                    display: "block",
                                  }}
                                >
                                  ✨ Excelled Topics
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                                  {questionAnalysis.excelled_topics.map((topic, index) => (
                                    <Chip
                                      key={index}
                                      label={topic}
                                      size="small"
                                      sx={{
                                        height: "24px",
                                        fontSize: "11px",
                                        fontWeight: 500,
                                        bgcolor: "rgba(102, 126, 234, 0.15)",
                                        color: "white",
                                        border: "1px solid rgba(102, 126, 234, 0.3)",
                                        "&:hover": {
                                          bgcolor: "rgba(102, 126, 234, 0.25)",
                                        },
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Challenging Topics */}
                            {questionAnalysis.challenging_topics && questionAnalysis.challenging_topics.length > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "rgba(255,255,255,0.5)",
                                    fontSize: "10px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    mb: 0.8,
                                    display: "block",
                                  }}
                                >
                                  ⚠️ Challenging Topics
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                                  {questionAnalysis.challenging_topics.map((topic, index) => (
                                    <Chip
                                      key={index}
                                      label={topic}
                                      size="small"
                                      sx={{
                                        height: "24px",
                                        fontSize: "11px",
                                        fontWeight: 500,
                                        bgcolor: "rgba(255, 255, 255, 0.05)",
                                        color: "rgba(255,255,255,0.7)",
                                        border: "1px solid rgba(255, 255, 255, 0.15)",
                                        "&:hover": {
                                          bgcolor: "rgba(255, 255, 255, 0.1)",
                                        },
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  // FALLBACK: Old format question_performance
                  const questionPerformance = parsedEvaluation?.question_performance;
                  
                  if (
                    questionPerformance &&
                    Array.isArray(questionPerformance) &&
                    questionPerformance.length > 0
                  ) {
                    return (
                      <Box
                        sx={{
                          mb: 2,
                          p: 3,
                          backgroundColor: "rgba(0,0,0,0.1)",
                          borderRadius: 2,
                          border: "1px solid rgba(102, 126, 234, 0.2)",
                        }}
                      >
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            background:
                              "linear-gradient(108deg, #667EEA 0%, #8859B8 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            fontWeight: 600,
                            mb: 3,
                          }}
                        >
                          💬 Question-by-Question Analysis
                        </Typography>

                        <Box sx={{ maxHeight: "400px", overflow: "auto" }}>
                          {questionPerformance.map((question, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 3,
                                p: 3,
                                backgroundColor: "rgba(0,0,0,0.15)",
                                borderRadius: 2,
                                border: "1px solid rgba(102, 126, 234, 0.1)",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600, color: "#667EEA" }}
                                >
                                  Question {question.question_id || index + 1}
                                </Typography>
                                <Box
                                  sx={{
                                    bgcolor:
                                      question.score >= 7
                                        ? "rgba(76, 175, 80, 0.2)"
                                        : question.score >= 4
                                        ? "rgba(255, 152, 0, 0.2)"
                                        : "rgba(244, 67, 54, 0.2)",
                                    color:
                                      question.score >= 7
                                        ? "#4CAF50"
                                        : question.score >= 4
                                        ? "#FF9800"
                                        : "#F44336",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontWeight: "bold",
                                  }}
                                >
                                  Score: {question.score || 0}/10
                                </Box>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: "rgba(255,255,255,0.7)",
                                  mb: 2,
                                  fontStyle: "italic",
                                  backgroundColor: "rgba(102, 126, 234, 0.05)",
                                  p: 2,
                                  borderRadius: 1,
                                }}
                              >
                                {question.question ||
                                  "Question text not available"}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: "rgba(255,255,255,0.9)",
                                  lineHeight: 1.6,
                                }}
                              >
                                <strong>Feedback:</strong>{" "}
                                {question.feedback || "No feedback provided"}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  }
                  
                  return null;
                })()}

                {/* NEW: Question Breakdown Cards */}
                {(() => {
                  const parsedEvaluation = parseEvaluationData(currentEvaluation.evaluation);
                  const questionBreakdown = parsedEvaluation?.question_analysis?.question_breakdown;
                  return questionBreakdown ? <QuestionBreakdownCards questionBreakdown={questionBreakdown} /> : null;
                })()}

                {/* NEW: Development Roadmap */}
                {(() => {
                  const parsedEvaluation = parseEvaluationData(currentEvaluation.evaluation);
                  const recommendations = parsedEvaluation?.interview_metadata?.development_recommendations;
                  return recommendations ? <DevelopmentRoadmap recommendations={recommendations} /> : null;
                })()}

                {/* Comprehensive Feedback - Compact */}
                {(() => {
                  const parsedEvaluation = parseEvaluationData(currentEvaluation.evaluation);
                  if (!parsedEvaluation) return null;

                  return (
                    <>
                      {parsedEvaluation.detailed_feedback && (
                        <Box sx={evaluationCardStyles}>
                          <Typography variant="overline" sx={headingStyles}>
                            Overall Assessment
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255,255,255,0.85)",
                              lineHeight: 1.5,
                              fontSize: "13px",
                              mt: 1,
                            }}
                          >
                            {parsedEvaluation.detailed_feedback}
                          </Typography>
                        </Box>
                      )}

                      {/* Key Strengths */}
                      {parsedEvaluation.candidate_strengths?.length > 0 && (
                        <Box sx={evaluationCardStyles}>
                          <Typography variant="overline" sx={headingStyles}>
                            Key Strengths
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1 }}>
                            {parsedEvaluation.candidate_strengths.map((strength, index) => (
                              <Chip
                                key={index}
                                label={strength}
                                size="small"
                                sx={{
                                  height: "24px",
                                  fontSize: "11px",
                                  fontWeight: 500,
                                  bgcolor: "rgba(102, 126, 234, 0.15)",
                                  color: "white",
                                  border: "1px solid rgba(102, 126, 234, 0.3)",
                                  "&:hover": {
                                    bgcolor: "rgba(102, 126, 234, 0.25)",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Areas for Development */}
                      {parsedEvaluation.areas_for_improvement?.length > 0 && (
                        <Box sx={evaluationCardStyles}>
                          <Typography variant="overline" sx={headingStyles}>
                            Areas for Development
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1 }}>
                            {parsedEvaluation.areas_for_improvement.map((area, index) => (
                              <Chip
                                key={index}
                                label={area}
                                size="small"
                                sx={{
                                  height: "24px",
                                  fontSize: "11px",
                                  fontWeight: 500,
                                  bgcolor: "rgba(255, 255, 255, 0.05)",
                                  color: "rgba(255,255,255,0.7)",
                                  border: "1px solid rgba(255, 255, 255, 0.15)",
                                  "&:hover": {
                                    bgcolor: "rgba(255, 255, 255, 0.1)",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                  );
                })()}

                {/* NEW EVALUATION FIELDS SECTION */}
                {(() => {
                  const parsedEvaluation = parseEvaluationData(
                    currentEvaluation.evaluation
                  );
                  
                  if (!parsedEvaluation) return null;

                  const hasNewFields = 
                    parsedEvaluation.hire_rationale ||
                    parsedEvaluation.development_recommendations ||
                    parsedEvaluation.risk_assessment ||
                    parsedEvaluation.key_strengths ||
                    parsedEvaluation.areas_of_concern ||
                    parsedEvaluation.specific_evidence ||
                    parsedEvaluation.overall_score ||
                    parsedEvaluation.recommendation;

                  if (!hasNewFields) return null;

                  // Styles are defined at the top of renderTabContent function
                  const fullWidthCardStyles = {
                    gridColumn: { xs: "1", lg: "1 / -1" },
                  };

                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                        gap: { xs: 2.5, md: 3 },
                        mt: 3,
                      }}
                    >
                      {/* Hire Rationale */}
                      {parsedEvaluation.hire_rationale && (
                        <Box
                          sx={{ ...evaluationCardStyles }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ ...headingStyles, mb: 2 }}
                          >
                            Hire Rationale
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "rgba(255,255,255,0.9)",
                              lineHeight: 1.6,
                            }}
                          >
                            {parsedEvaluation.hire_rationale}
                          </Typography>
                        </Box>
                      )}

                      {/* Key Strengths */}
                      {parsedEvaluation.key_strengths && parsedEvaluation.key_strengths.length > 0 && (
                        <Box
                          sx={{ ...evaluationCardStyles }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ ...headingStyles, mb: 2 }}
                          >
                            Key Strengths
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {parsedEvaluation.key_strengths.map((strength, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    background: "#FFFFFF",
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "rgba(255,255,255,0.9)",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {strength}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Areas of Concern */}
                      {parsedEvaluation.areas_of_concern && parsedEvaluation.areas_of_concern.length > 0 && (
                        <Box
                          sx={{ ...evaluationCardStyles }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ ...headingStyles, mb: 2 }}
                          >
                            Areas of Concern
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {parsedEvaluation.areas_of_concern.map((concern, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    background: "rgba(255, 255, 255, 0.6)",
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "rgba(255,255,255,0.9)",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {concern}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Development Recommendations */}
                      {parsedEvaluation.development_recommendations && parsedEvaluation.development_recommendations.length > 0 && (
                        <Box
                          sx={{ ...evaluationCardStyles }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ ...headingStyles, mb: 2 }}
                          >
                            Development Recommendations
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {parsedEvaluation.development_recommendations.map((recommendation, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    background: "#FFFFFF",
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "rgba(255,255,255,0.9)",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {recommendation}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Risk Assessment */}
                      {parsedEvaluation.risk_assessment && (
                        <Box
                          sx={{
                            ...evaluationCardStyles,
                            ...fullWidthCardStyles,
                            border: "1px solid rgba(255, 255, 255, 0.15)",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ ...headingStyles, mb: 2 }}
                          >
                            Risk Assessment
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "rgba(255,255,255,0.9)",
                              lineHeight: 1.6,
                            }}
                          >
                            {parsedEvaluation.risk_assessment}
                          </Typography>
                        </Box>
                      )}

                      {/* Specific Evidence */}
                      {parsedEvaluation.specific_evidence && parsedEvaluation.specific_evidence.length > 0 && (
                        <Box
                          sx={{
                            ...evaluationCardStyles,
                            ...fullWidthCardStyles,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ ...headingStyles, mb: 2 }}
                          >
                            Specific Evidence
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {parsedEvaluation.specific_evidence.map((evidence, index) => (
                              <Box
                                key={index}
                                sx={{
                                  p: 2.5,
                                  background: "rgba(255,255,255,0.03)",
                                  borderRadius: "14px",
                                  border: "1px solid rgba(255, 255, 255, 0.06)",
                                  boxShadow: "0 10px 24px rgba(0, 0, 0, 0.28)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "rgba(255,255,255,0.82)",
                                    lineHeight: 1.5,
                                    letterSpacing: "0.01em",
                                  }}
                                >
                                  {evidence}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 8,
                  color: "rgba(255,255,255,0.6)",
                }}
              >
                <AssessmentIcon
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    color: "rgba(102, 126, 234, 0.4)",
                  }}
                />
                <Typography variant="h6">No Evaluation Available</Typography>
                <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                  This candidate hasn't been evaluated yet.
                </Typography>
              </Box>
            )}
          </Box>
        );

      case "transcript":
        return (
          <Box sx={{ p: "16px" }}>
            {profiles?.[0]?.candidateContextTextFile ? (
              <TranscriptViewer url={profiles[0].candidateContextTextFile} />
            ) : currentEvaluation?.transcript ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                }}
              >
                {(() => {
                  try {
                    let transcriptData;
                    
                    // Handle both JSON array and string formats
                    if (typeof currentEvaluation.transcript === 'string') {
                      // Try to parse as JSON first
                      try {
                        transcriptData = JSON.parse(currentEvaluation.transcript);
                        // If it's an array, use it directly
                        if (!Array.isArray(transcriptData)) {
                          // If it's not an array, it might be the old string format
                          transcriptData = currentEvaluation.transcript.split('\n').map((line, idx) => ({
                            type: 'unknown',
                            content: line,
                            timestamp: `${idx * 10}s`
                          }));
                        }
                      } catch (jsonError) {
                        // If JSON parsing fails, treat as old string format
                        transcriptData = currentEvaluation.transcript.split('\n').map((line, idx) => ({
                          type: 'unknown',
                          content: line,
                          timestamp: `${idx * 10}s`
                        }));
                      }
                    } else if (Array.isArray(currentEvaluation.transcript)) {
                      transcriptData = currentEvaluation.transcript;
                    } else {
                      transcriptData = [];
                    }
                    
                    if (!Array.isArray(transcriptData)) {
                      return (
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                          Invalid transcript format
                        </Typography>
                      );
                    }
                    
                    return transcriptData.map((item, idx) => {
                      // Handle different transcript item types
                      let speaker = "";
                      let message = "";
                      let itemStyle = {};

                      switch (item.type) {
                        case "ai_question":
                          speaker = "AI Interviewer";
                          message = item.content;
                          itemStyle = { 
                            bgcolor: "rgba(102, 126, 234, 0.15)", 
                            borderLeft: "3px solid rgba(102, 126, 234, 0.8)",
                            p: "12px 14px",
                            borderRadius: "12px",
                            border: "1px solid rgba(102, 126, 234, 0.2)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                          };
                          break;
                        case "user_response":
                          speaker = "Candidate";
                          message = item.content;
                          itemStyle = { 
                            bgcolor: "rgba(136, 89, 184, 0.15)", 
                            borderLeft: "3px solid rgba(136, 89, 184, 0.8)",
                            p: "12px 14px",
                            borderRadius: "12px",
                            border: "1px solid rgba(136, 89, 184, 0.2)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                          };
                          break;
                        case "interview_termination":
                          speaker = "System";
                          message = item.message || item.content || "Interview terminated";
                          itemStyle = { 
                            bgcolor: "rgba(244, 67, 54, 0.15)", 
                            borderLeft: "3px solid rgba(244, 67, 54, 0.8)",
                            p: "12px 14px",
                            borderRadius: "12px",
                            border: "1px solid rgba(244, 67, 54, 0.2)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                          };
                          break;
                        default:
                          // Handle old format or unknown types
                          if (item.speaker && item.message) {
                            // Old format with speaker/message
                            speaker = item.speaker;
                            message = item.message;
                          } else {
                            // Unknown format
                            speaker = item.type || "Unknown";
                            message = item.content || item.message || JSON.stringify(item);
                          }
                          itemStyle = { 
                            bgcolor: "rgba(255, 255, 255, 0.08)", 
                            borderLeft: "3px solid rgba(255, 255, 255, 0.4)",
                            p: "12px 14px",
                            borderRadius: "12px",
                            border: "1px solid rgba(255, 255, 255, 0.15)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                          };
                      }

                      return (
                        <Box key={idx} sx={itemStyle}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                            <Typography sx={{ 
                              color: "rgba(102,126,234,0.95)", 
                              fontWeight: 600,
                              fontSize: "11px",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                            }}>
                              {speaker}
                            </Typography>
                            {item.timestamp && (
                              <Typography sx={{ 
                                color: "rgba(255,255,255,0.5)",
                                fontSize: "10px",
                                fontWeight: 500,
                              }}>
                                {item.timestamp}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ 
                              color: "rgba(255,255,255,0.85)",
                              lineHeight: 1.5,
                              fontSize: "13px",
                              whiteSpace: "pre-wrap"
                            }}
                          >
                            {message}
                          </Typography>
                          {item.question_number && (
                            <Typography sx={{ 
                              color: "rgba(255,255,255,0.4)",
                              fontSize: "10px",
                              mt: 0.8,
                              display: "block",
                              fontWeight: 500,
                            }}>
                              Q#{item.question_number}
                            </Typography>
                          )}
                        </Box>
                      );
                    });
                  } catch (error) {
                    console.error("Error parsing transcript:", error);
                    return (
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                        Error parsing transcript data
                      </Typography>
                    );
                  }
                })()}
              </Box>
            ) : (
              <Typography variant="body1">No transcript available</Typography>
            )}
          </Box>
        );

      // KNOWLEDGE GRAPH TAB - COMMENTED OUT
      /*
      case "knowledge-graph":
        return (
          <Box sx={{ p: 2, height: "100%" }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                background: "linear-gradient(108deg, #667EEA 0%, #8859B8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                borderBottom: "1px solid rgba(153, 0, 255, 0.3)",
                pb: 1,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Knowledge Graph
            </Typography>
            <Box
              sx={{
                height: "calc(100% - 60px)",
                border: "1px solid rgba(153, 0, 255, 0.2)",
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "rgba(0,0,0,0.3)",
              }}
            >
              <ErrorBoundary>
                <CandidateViewKnowledgeGraph
                  candidateEmail={email}
                  preloadedCandidate={prepareCandidateDataForKG()}
                  height="100%"
                />
              </ErrorBoundary>
            </Box>
            
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  openKnowledgeGraphPage();
                }}
                sx={{
                  color: "#667EEA",
                  borderColor: "#667EEA",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.1)",
                    borderColor: "#8859B8",
                  },
                }}
              >
                Open Full Screen View
              </Button>
            </Box>
          </Box>
        );
      */

      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography>Select a tab to view content</Typography>
          </Box>
        );
    }
  };

  // Add these helper functions inside the CompanyCandidateViewProfilev1 component
  // Function to get the evaluation data
  const getEvaluationData = () => {
    const evaluation = getCurrentEvaluationData();
    if (!evaluation?.evaluation) {
      return null;
    }

    try {
      return typeof evaluation.evaluation === "string"
        ? JSON.parse(evaluation.evaluation)
        : evaluation.evaluation;
    } catch (error) {
      console.error("Error parsing evaluation data:", error);
      return null;
    }
  };

  // Function to find criterion key that matches a formatted label
  const findCriterionKeyByLabel = (evalData, formattedLabel) => {
    return Object.keys(evalData).find((key) => {
      const label = key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return label === formattedLabel;
    });
  };

  // Add this handleChartClick function

  // Add new state and handler
  const [relatedCandidates] = useState([]);

  const handleCandidateClick = (candidate) => {
    // Handle navigation to candidate's profile/video
    navigate(`/company/candidate/${candidate.id}`);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          bgcolor: "black",
          overflow: "hidden",
          position: "relative",
          pt: "64px", // Add padding-top to account for the header height
        }}
      >
        {/* Debug Data Inspector - Remove this in production
        <DebugDataInspector 
          interviewEvaluations={interviewEvaluations}
          profiles={profiles}
          selectedSlotId={selectedSlotId}
          title="Video Data Inspector"
        /> */}

        {/* Add the CustomHeader */}
        {/* <CustomHeader fullName={fullName} email={email} /> */}

        <div
          style={{
            position: "absolute",
            top: 16,
            left: 32,
            zIndex: 2,
            padding: "8px 16px",
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/logogetscreened.svg`}
            alt="GetScreened"
            style={{
              height: "32px",
              width: "auto",
              objectFit: "contain",
            }}
          />
        </div>

        {/* Toggle controls removed - now handled by AI Score Panel */}
{/* Background Video */}
        <VideoPlayer
          profiles={profiles}
          interviewEvaluations={interviewEvaluations}
          selectedSlotId={selectedSlotId}
        />

        {/* MODIFIED: Profile Overlay now controlled by parent state */}
        <ProfileOverlay
          expanded={profileExpanded}
          fullName={fullName}
          photoURL={photoURL}
          workStatus={workStatus}
          keySkills={keySkills}
          description={description}
          onMinimize={() => setProfileExpanded(false)}
          mobileNumber={mobileNumber}
          email={email}
          linkedinURL={linkedinURL}
          githubURL={githubURL}
          currentCity={currentCity}
          workPreference={workPreference}
          preferredSalary={preferredSalary}
          educationDetails={educationDetails}
          roleApplyFor={roleApplyFor}
        />

        {/* MODIFIED: Evaluation Panel now controlled by parent state */}
        {interviewEvaluations &&
          interviewEvaluations.length > 0 &&
          currentEvaluation?.evaluation && (
            <EvaluationOverlay
              expanded={chartExpanded}
              onMinimize={() => setChartExpanded(false)}
              radarChartData={radarChartData}
              evaluationData={parseEvaluationData(currentEvaluation?.evaluation)}
              onViewFullEvaluation={() => {
                setIsDrawerOpen(true);
                setChartExpanded(false);
              }}
            />
          )}

        {/* Charts View Overlay - Side-by-side chart panel */}
        {interviewEvaluations &&
          interviewEvaluations.length > 0 &&
          currentEvaluation?.evaluation && (
            <ChartsOverlay
              expanded={chartsViewExpanded}
              onMinimize={() => setChartsViewExpanded(false)}
              radarChartData={radarChartData}
              technicalRadarChartData={getRadarChartData('technical')}
              evaluationData={parseEvaluationData(currentEvaluation?.evaluation)}
            />
          )}

        {/* Side Drawer */}
        {/* Full Evaluation Overlay */}
        <Box
          sx={{
            position: "fixed",
            top: 64,
            right: 24,
            width: { xs: "90vw", sm: "500px", md: "550px" },
            maxHeight: "calc(100vh - 88px)",
            background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
            backdropFilter: "blur(32px)",
            WebkitBackdropFilter: "blur(32px)",
            borderRadius: "20px",
            border: "1px solid rgba(102, 126, 234, 0.28)",
            boxShadow: "0 25px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
            zIndex: 100030,
            transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isDrawerOpen ? "translateX(0)" : "translateX(120%)",
            opacity: isDrawerOpen ? 1 : 0,
            visibility: isDrawerOpen ? "visible" : "hidden",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Compact Header */}
          <Box
            sx={{
              position: "relative",
              padding: "12px 16px 10px",
              borderBottom: "1px solid rgba(102, 126, 234, 0.18)",
              flexShrink: 0,
            }}
          >
            <IconButton
              onClick={() => setIsDrawerOpen(false)}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                color: "rgba(255,255,255,0.7)",
                bgcolor: "rgba(255,255,255,0.08)",
                width: 28,
                height: 28,
                zIndex: 2,
                "&:hover": {
                  bgcolor: "rgba(102,126,234,0.25)",
                  color: "white",
                },
                transition: "all 0.2s",
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
            
            <Typography
              variant="h6"
              sx={{
                color: "white",
                fontWeight: 600,
                fontSize: "13px",
                letterSpacing: "0.02em",
                pr: 4,
              }}
            >
              {fullName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(102,126,234,0.8)",
                fontSize: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontWeight: 600,
              }}
            >
              Detailed Analysis
            </Typography>
          </Box>

          {/* Compact Tabs */}
          <Tabs
            value={activeTab === "evaluation" ? 0 : activeTab === "transcript" ? 1 : 0}
            onChange={(_, newValue) =>
              setActiveTab(newValue === 0 ? "evaluation" : "transcript")
            }
            variant="fullWidth"
            sx={{
              minHeight: "42px",
              borderBottom: "1px solid rgba(102, 126, 234, 0.15)",
              background: "rgba(255,255,255,0.05)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              flexShrink: 0,
              "& .MuiTabs-indicator": {
                height: "2px",
                background: "linear-gradient(90deg, #667EEA 0%, #8859B8 100%)",
                borderRadius: "2px 2px 0 0",
              },
              "& .MuiTab-root": {
                color: "rgba(255,255,255,0.6)",
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "none",
                minHeight: "42px",
                letterSpacing: "0.02em",
                "&.Mui-selected": {
                  color: "rgba(102,126,234,0.95)",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "16px",
                  mr: 0.8,
                },
              },
            }}
          >
            <Tab icon={<AssessmentIcon />} label="Evaluation" iconPosition="start" />
            <Tab icon={<FormatQuoteIcon />} label="Transcript" iconPosition="start" />
          </Tabs>

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255,255,255,0.03)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(102,126,234,0.3)",
                borderRadius: "10px",
                "&:hover": {
                  background: "rgba(102,126,234,0.5)",
                },
              },
            }}
          >
            {renderTabContent()}
          </Box>
        </Box>
        {/* Add this Modal component at the end of the CompanyCandidateViewProfilev1 component return statement, 
        right before the closing </ThemeProvider> tag: */}
        <Modal
          open={showDetailPopup}
          onClose={() => setShowDetailPopup(false)}
          aria-labelledby="skill-detail-modal"
          sx={{ zIndex: 100025 }} // Highest z-index to ensure it's above everything
          backdropProps={{
            style: { backgroundColor: "rgba(0,0,0,0.7)" },
          }}
        >
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 400 },
              background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(56,98,140,0.18) 45%, rgba(18,30,48,0.16) 100%)",
              backdropFilter: "blur(32px)",
              WebkitBackdropFilter: "blur(32px)",
              border: "1px solid rgba(153, 0, 255, 0.3)",
              borderRadius: "16px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15)",
              p: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                sx={{ color: "white" }}
              >
                {selectedSkill?.name}
              </Typography>
              <IconButton
                onClick={() => setShowDetailPopup(false)}
                sx={{ color: "white", p: 0 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  border: "3px solid #9900FF",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  bgcolor: "rgba(153, 0, 255, 0.1)",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "white" }}
                >
                  {selectedSkill?.score}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255, 255, 255, 0.6)" }}
                >
                  out of {selectedSkill?.maxScore}
                </Typography>
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ color: "#9900FF", mb: 1 }}>
              Evaluator Comments:
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                maxHeight: "200px",
                overflowY: "auto",
                p: 2,
                bgcolor: "rgba(0, 0, 0, 0.3)",
                borderRadius: 1,
              }}
            >
              {selectedSkill?.comments}
            </Typography>
          </Paper>
        </Modal>
        {/* Add RelatedCandidates component after the logo */}
        {/* <RelatedCandidates
          candidates={relatedCandidates}
          onCandidateClick={handleCandidateClick}
        /> */}

        {/* Draggable Toggle Buttons */}
        <DraggableToggleButtons
          profileExpanded={profileExpanded}
          chartExpanded={chartExpanded}
          chartsViewExpanded={chartsViewExpanded}
          onProfileToggle={() => setProfileExpanded(!profileExpanded)}
          onChartToggle={() => setChartExpanded(!chartExpanded)}
          onChartsViewToggle={() => setChartsViewExpanded(!chartsViewExpanded)}
          hasEvaluations={interviewEvaluations && interviewEvaluations.length > 0}
        />
      </Box>
    </ThemeProvider>
  );
};

// Component to fetch and display transcript
const TranscriptViewer = ({ url }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTranscript = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch transcript: ${response.statusText}`);
        }
        const content = await response.text();
        setText(content);
      } catch (err) {
        console.error("Error fetching transcript:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchTranscript();
    } else {
      setLoading(false);
      setError("No transcript URL provided");
    }
  }, [url]);

  if (loading) {
    return <CircularProgress size={24} />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box
      sx={{
        maxHeight: "70vh",
        overflow: "auto",
        bgcolor: "rgba(0,0,0,0.2)",
        p: 2,
        borderRadius: 1,
        whiteSpace: "pre-wrap",
      }}
    >
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
};

// Enhanced AI Score Panel Component with Integrated Toggle Controls
const AIScorePanel = ({ 
  score = 85, 
  profileExpanded = false,
  chartExpanded = false,
  onProfileToggle,
  onChartToggle,
  hasEvaluations = false
}) => {
  const normalized = Math.min(100, Math.max(0, Number(score) || 0));
  const [hovered, setHovered] = useState(false);
  
  const getScoreColor = (score) => {
    if (score >= 90) return { primary: "#00E676", secondary: "#69F0AE" };
    if (score >= 80) return { primary: "#67e8a1", secondary: "#A5D6A7" };
    if (score >= 70) return { primary: "#FFC107", secondary: "#FFE082" };
    if (score >= 60) return { primary: "#ffd166", secondary: "#FFECB3" };
    return { primary: "#ff6b6b", secondary: "#FFCDD2" };
  };

  const scoreColors = getScoreColor(normalized);

  // Force component refresh to clear any cached CountUp references
  useEffect(() => {
    // This useEffect ensures the component properly re-renders without CountUp
  }, [normalized]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 99999, // Increased z-index to ensure it's always on top
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* Main AI Score Panel Container with Side Buttons */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Profile Toggle Button - Left Side (9 o'clock) */}
        <Box
          sx={{
            position: "absolute",
            left: -18,
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            zIndex: 100000,
            "&:hover": {
              transform: "translateY(-50%) scale(1.15)",
            },
          }}
          onClick={onProfileToggle}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: profileExpanded 
                ? "linear-gradient(135deg, #e9c46a 0%, #f4a261 100%)"
                : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(20px)",
              border: profileExpanded 
                ? "2px solid rgba(233, 196, 106, 0.8)"
                : "2px solid rgba(255, 255, 255, 0.2)",
              boxShadow: profileExpanded
                ? "0 6px 20px rgba(233, 196, 106, 0.5)"
                : "0 4px 12px rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.4s ease",
              "&:hover": {
                boxShadow: profileExpanded
                  ? "0 8px 25px rgba(233, 196, 106, 0.6)"
                  : "0 6px 20px rgba(233, 196, 106, 0.5)",
                border: "2px solid rgba(233, 196, 106, 0.9)",
              },
            }}
          >
            <PersonIcon 
              sx={{ 
                fontSize: 16, 
                color: profileExpanded ? "white" : "rgba(255,255,255,0.9)",
                filter: profileExpanded ? "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" : "none",
              }} 
            />
            
            {/* Active Indicator Dot */}
            {profileExpanded && (
              <Box
                sx={{
                  position: "absolute",
                  top: -2,
                  right: -2,
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #00E676 0%, #69F0AE 100%)",
                  border: "1px solid rgba(0,0,0,0.8)",
                  boxShadow: "0 0 8px rgba(0, 230, 118, 0.6)",
                  animation: "activePulse 2s ease-in-out infinite",
                  "@keyframes activePulse": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      opacity: 1,
                    },
                    "50%": {
                      transform: "scale(1.3)",
                      opacity: 0.8,
                    },
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Evaluation Toggle Button - Bottom (6 o'clock) */}
        {hasEvaluations && (
          <Box
            sx={{
              position: "absolute",
              bottom: -18,
              left: "50%",
              transform: "translateX(-50%)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              zIndex: 100000,
              "&:hover": {
                transform: "translateX(-50%) scale(1.15)",
              },
            }}
            onClick={onChartToggle}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: chartExpanded 
                  ? "linear-gradient(135deg, #2a9d8f 0%, #264653 100%)"
                  : "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(20px)",
                border: chartExpanded 
                  ? "2px solid rgba(42, 157, 143, 0.8)"
                  : "2px solid rgba(255, 255, 255, 0.2)",
                boxShadow: chartExpanded
                  ? "0 6px 20px rgba(42, 157, 143, 0.5)"
                  : "0 4px 12px rgba(0,0,0,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.4s ease",
                "&:hover": {
                  boxShadow: chartExpanded
                    ? "0 8px 25px rgba(42, 157, 143, 0.6)"
                    : "0 6px 20px rgba(42, 157, 143, 0.5)",
                  border: "2px solid rgba(42, 157, 143, 0.9)",
                },
              }}
            >
              <AssessmentIcon 
                sx={{ 
                  fontSize: 16, 
                  color: chartExpanded ? "white" : "rgba(255,255,255,0.9)",
                  filter: chartExpanded ? "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" : "none",
                }} 
              />
              
              {/* Active Indicator Dot */}
              {chartExpanded && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #00E676 0%, #69F0AE 100%)",
                    border: "1px solid rgba(0,0,0,0.8)",
                    boxShadow: "0 0 8px rgba(0, 230, 118, 0.6)",
                    animation: "activePulse 2s ease-in-out infinite",
                    "@keyframes activePulse": {
                      "0%, 100%": {
                        transform: "scale(1)",
                        opacity: 1,
                      },
                      "50%": {
                        transform: "scale(1.3)",
                        opacity: 0.8,
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Main AI Score Panel - Properly Aligned Concentric Circles */}
        <Box
          sx={{
            position: "relative",
            width: 150,
            height: 150,
            cursor: "pointer",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: hovered ? "scale(1.03)" : "scale(1)",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Outermost Circle - Background with blur */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(20px)",
              borderRadius: "50%",
              border: `2px solid rgba(255, 255, 255, 0.1)`,
              boxShadow: `0 15px 50px rgba(0,0,0,0.7)`,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.7)",
                boxShadow: `0 25px 70px rgba(0,0,0,0.8)`,
                border: "2px solid rgba(255, 255, 255, 0.15)",
              },
            }}
          />

          {/* Middle Circle - Progress Ring Container */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 105,
              height: 105,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Background Progress Circle */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={140}
              thickness={4}
              sx={{ 
                position: "absolute",
                color: "rgba(255,255,255,0.08)",
                filter: "drop-shadow(0 0 8px rgba(255,255,255,0.1))",
              }}
            />
            
            {/* Active Progress Circle with Gradient */}
            <CircularProgress
              variant="determinate"
              value={normalized}
              size={140}
              thickness={4}
              sx={{
                position: "absolute",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                  stroke: `url(#aiScoreGradient)`,
                  filter: `drop-shadow(0 0 15px ${scoreColors.primary}70)`,
                },
              }}
            />
          </Box>

          {/* Inner Circle - Content Container */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 90,
              height: 90,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.3)",
              border: `1px solid ${scoreColors.primary}20`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            {/* AI Score Label */}
            <Typography
              variant="caption"
              sx={{ 
                color: "rgba(255,255,255,0.9)", 
                fontWeight: 800, 
                fontSize: 10,
                letterSpacing: "1px",
                textShadow: "0 2px 8px rgba(0,0,0,0.8)",
              }}
            >
              SCORE
            </Typography>

            {/* Score Number */}
            <Typography
              variant="h4"
              sx={{ 
                color: "#fff", 
                fontWeight: "900", 
                fontSize: hovered ? "1.8rem" : "1.5rem",
                transition: "font-size 0.3s ease",
                textShadow: `0 0 25px ${scoreColors.primary}90, 0 3px 10px rgba(0,0,0,0.8)`,
                filter: "drop-shadow(0 0 10px rgba(255,255,255,0.4))",
                lineHeight: 1,
              }}
            >
              {normalized}
            </Typography>
          </Box>

          {/* Enhanced SVG Gradient */}
          <svg style={{ width: 0, height: 0, position: "absolute" }}>
            <defs>
              <linearGradient id="aiScoreGradient" gradientTransform="rotate(45)">
                <stop offset="0%" stopColor={scoreColors.primary} />
                <stop offset="50%" stopColor={scoreColors.secondary} />
                <stop offset="100%" stopColor={scoreColors.primary} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
          </svg>

          {/* Animated Pulse Ring */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 180,
              height: 180,
              borderRadius: "50%",
              border: `2px solid ${scoreColors.primary}30`,
              animation: "aiScorePulse 3s ease-in-out infinite",
              "@keyframes aiScorePulse": {
                "0%, 100%": {
                  transform: "translate(-50%, -50%) scale(1)",
                  opacity: 0.3,
                },
                "50%": {
                  transform: "translate(-50%, -50%) scale(1.05)",
                  opacity: 0.1,
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export { CompanyCandidateViewProfilev1Wrapper };
export default CompanyCandidateViewProfilev1;